import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleAddFieldTableRow,
  handleDeleteFieldTableRow,
  handleEditFieldTableRow,
  handleFieldTableChange,
  handleFormChange,
  handleSaveFieldTableRow,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  Checkbox,
  FieldTable,
  Input,
  RadioButton,
  Select,
  TextArea,
  ToggleSwitch,
} from "../common/ControlBundle";
import ExpandDiv from "../common/ExpandDiv";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function NVCMHA() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(false);
  const [showNarrative, setShowNarrative] = useState("Hide");

  const preSignForm = () => {
    const obj = { ...data };

    const familyInformationDetails = JSON.parse(
      obj.FamilyInformationDetails["@value"]
    );

    if (familyInformationDetails)
      obj.FamilyInformationDetails["@value"] = JSON.stringify(
        familyInformationDetails.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const bioFamilyInfo = JSON.parse(obj.BioFamilyInfo["@value"]);

    if (bioFamilyInfo)
      obj.BioFamilyInfo["@value"] = JSON.stringify(
        bioFamilyInfo.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const placementHistory = JSON.parse(obj.PlacementHistory["@value"]);

    if (placementHistory)
      obj.PlacementHistory["@value"] = JSON.stringify(
        placementHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const mentalHealthDiagnoses = JSON.parse(
      obj.MentalHealthDiagnoses["@value"]
    );

    if (mentalHealthDiagnoses)
      obj.MentalHealthDiagnoses["@value"] = JSON.stringify(
        mentalHealthDiagnoses.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const mentalHealthTreatmentHistory = JSON.parse(
      obj.MentalHealthTreatmentHistory["@value"]
    );

    if (mentalHealthTreatmentHistory)
      obj.MentalHealthTreatmentHistory["@value"] = JSON.stringify(
        mentalHealthTreatmentHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const substanceUseHistory = JSON.parse(obj.SubstanceUseHistory["@value"]);

    if (substanceUseHistory)
      obj.SubstanceUseHistory["@value"] = JSON.stringify(
        substanceUseHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const substanceUseTreatmentHistory = JSON.parse(
      obj.SubstanceUseTreatmentHistory["@value"]
    );

    if (substanceUseTreatmentHistory)
      obj.SubstanceUseTreatmentHistory["@value"] = JSON.stringify(
        substanceUseTreatmentHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const diagnosisDetails = JSON.parse(obj.DiagnosisDetails["@value"]);

    if (diagnosisDetails)
      obj.DiagnosisDetails["@value"] = JSON.stringify(
        diagnosisDetails.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const treatmentRecommendations = JSON.parse(
      obj.TreatmentRecommendations["@value"]
    );

    if (treatmentRecommendations)
      obj.TreatmentRecommendations["@value"] = JSON.stringify(
        treatmentRecommendations.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    setData(obj);
  };

  const handleExpandStatusChange = (overrideValue, callback) => {
    if (overrideValue !== undefined) {
      setExpandStatus(overrideValue);
      if (callback) {
        window.setTimeout(callback, 5);
      }
    } else setExpandStatus(!expandStatus);
  };

  const getPlacementType = (item) => {
    if (item.TFC === "true") return "Treatment Foster Care";
    if (item.Kinship === "true") return "Kinship";

    if (item.DFS === "true") return "DFS";

    if (item.Hospital === "true") return "Hospital";

    if (item.EmergencyShelter === "true") return "Emergency Shelter";

    if (item.BiologicalHome === "true") return "Biological Home";

    if (item.ResidentialTreatmentCenter === "true")
      return "Residential Treatment Center";

    if (item.Other === "true")
      return item.OtherDetails !== undefined ? item.OtherDetails : "Other";

    return "";
  };

  const handleSubstanceUseNAChange = (e) => {
    handleChange(e);
    const noPastTreatmentInput = {
      currentTarget: {
        type: "checkbox",
        name: "NoPastTreatment",
        checked: true,
      },
    };
    handleChange(noPastTreatmentInput);
  };

  const psdTypeOptions = [
    { _id: "Initial", name: "Initial" },
    { _id: "Review", name: "Review/Update" },
  ];

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const yesNoSusOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "Suspected", name: "Suspected" },
  ];

  const ageCategoryOptions = [
    { _id: "0-3", name: "0-3 years old" },
    { _id: "4-10", name: "4-10 years old" },
    { _id: "11-18", name: "11-18 years old" },
    { _id: "19-24", name: "19-24 years old" },
    { _id: "25+", name: "25+ years old" },
  ];

  const assessmentTypeOptions = [
    { _id: "ECSII", name: "ECSII" },
    { _id: "CASII", name: "CASII" },
    { _id: "LOCUS", name: "LOCUS" },
  ];

  const treatmentRecommendationOptions = [
    {
      _id: "Individual Therapy",
      name: "Individual Therapy",
    },
    { _id: "Family Therapy", name: "Family Therapy" },
    { _id: "Occupational/Speech Therapy", name: "Occupational/Speech Therapy" },
    { _id: "Psychiatric Assessment", name: "Psychiatric Assessment" },
    { _id: "Safety Plan", name: "Safety Plan" },
    {
      _id: "IEP/504 Educational Supports",
      name: "IEP/504 Educational Supports",
    },
    { _id: "Placement Recommendations", name: "Placement Recommendations" },
    {
      _id: "Extra Curricular/Recreational Activities",
      name: "Extra Curricular/Recreational Activities",
    },
    { _id: "Other", name: "Other" },
  ];

  const pronounsOptions = [
    {
      _id: "She/Her/Hers",
      name: "She/Her/Hers",
    },
    { _id: "He/Him/His", name: "He/Him/His" },
    { _id: "They/Them/Their", name: "They/Them/Their" },
    { _id: "Describe", name: "Prefer to self-describe" },
    { _id: "Decline", name: "Prefer not to say" },
  ];

  const identifiedRaceOptions = [
    {
      _id: "American Indian or Alaskan Native",
      name: "American Indian or Alaskan Native",
    },
    {
      _id: "Native Hawaiian or Other Pacific Islander",
      name: "Native Hawaiian or Other Pacific Islander",
    },
    { _id: "Black or African American", name: "Black or African American" },
    { _id: "White", name: "White" },
    { _id: "Asian", name: "Asian" },
    { _id: "Hispanic", name: "Hispanic" },
    { _id: "Middle Eastern", name: "Middle Eastern" },
    {
      _id: "Native American or Indigenous",
      name: "Native American or Indigenous",
    },
    { _id: "Multi-cultural", name: "Multi-cultural" },
    { _id: "Other", name: "Other" },
  ];

  const qualityOptions = [
    {
      _id: "Close",
      name: "Close",
    },
    {
      _id: "Conflictual",
      name: "Conflictual",
    },
    { _id: "Distant", name: "Distant" },
    { _id: "Cooperative", name: "Cooperative" },
    { _id: "Broken", name: "Broken" },
    { _id: "Estranged", name: "Estranged" },
    { _id: "Strong", name: "Strong" },
    { _id: "Indifferent", name: "Indifferent" },
  ];

  const mentalHealthDiagnosisStatusOptions = [
    {
      _id: "Current",
      name: "Current",
    },
    {
      _id: "Past",
      name: "Past",
    },
  ];

  const educationOptions = [
    {
      _id: "Client has not started school",
      name: "Client has not started school (skip to medical history section)",
    },
    {
      _id: "Currently enrolled in school",
      name: "Currently enrolled in school",
    },
    { _id: "Graduated", name: "Graduated" },
    { _id: "GED", name: "GED" },
    { _id: "Dropout/Did not complete", name: "Dropout/Did not complete" },
  ];

  const customValidate = (errs) => {
    if (
      (!data.ISClient["@value"] || data.ISClient["@value"] === "false") &&
      (!data.ISParent["@value"] || data.ISParent["@value"] === "false") &&
      (!data.ISFamily["@value"] || data.ISFamily["@value"] === "false") &&
      (!data.ISGuardian["@value"] || data.ISGuardian["@value"] === "false") &&
      (!data.ISCaregiver["@value"] || data.ISCaregiver["@value"] === "false") &&
      (!data.ISPhysician["@value"] || data.ISPhysician["@value"] === "false") &&
      (!data.ISRecords["@value"] || data.ISRecords["@value"] === "false") &&
      (!data.ISServiceProvider["@value"] ||
        data.ISServiceProvider["@value"] === "false") &&
      (!data.ISSchool["@value"] || data.ISSchool["@value"] === "false") &&
      (!data.ISCourt["@value"] || data.ISCourt["@value"] === "false") &&
      (!data.ISDFS["@value"] || data.ISDFS["@value"] === "false") &&
      (!data.ISOther["@value"] || data.ISOther["@value"] === "false")
    ) {
      if (errs)
        errs.InformationSources =
          "At least one Information Source is required.";
      else
        errs = {
          InformationSources: "At least one Information Source is required.",
        };
    }

    if (
      (!data.AppropriateAppearance["@value"] ||
        data.AppropriateAppearance["@value"] === "false") &&
      (!data.Neat["@value"] || data.Neat["@value"] === "false") &&
      (!data.Disheveled["@value"] || data.Disheveled["@value"] === "false") &&
      (!data.BizarreAppearance["@value"] ||
        data.BizarreAppearance["@value"] === "false") &&
      (!data.OtherAppearance["@value"] ||
        data.OtherAppearance["@value"] === "false")
    ) {
      if (errs) errs.MSAppearance = "At least one value is required.";
      else
        errs = {
          MSAppearance: "At least one value is required.",
        };
    }

    if (
      (!data.Cooperative["@value"] || data.Cooperative["@value"] === "false") &&
      (!data.Guarded["@value"] || data.Guarded["@value"] === "false") &&
      (!data.Hyperactive["@value"] || data.Hyperactive["@value"] === "false") &&
      (!data.Agitated["@value"] || data.Agitated["@value"] === "false") &&
      (!data.Paranoid["@value"] || data.Paranoid["@value"] === "false") &&
      (!data.Tics["@value"] || data.Tics["@value"] === "false") &&
      (!data.Impulsive["@value"] || data.Impulsive["@value"] === "false") &&
      (!data.Aggressive["@value"] || data.Aggressive["@value"] === "false") &&
      (!data.Withdrawn["@value"] || data.Withdrawn["@value"] === "false") &&
      (!data.BizarreBehavior["@value"] ||
        data.BizarreBehavior["@value"] === "false") &&
      (!data.Rigidity["@value"] || data.Rigidity["@value"] === "false") &&
      (!data.OtherBehavior["@value"] ||
        data.OtherBehavior["@value"] === "false")
    ) {
      if (errs) errs.MSBehavior = "At least one value is required.";
      else
        errs = {
          MSBehavior: "At least one value is required.",
        };
    }

    if (
      (!data.Normal["@value"] || data.Normal["@value"] === "false") &&
      (!data.Slurred["@value"] || data.Slurred["@value"] === "false") &&
      (!data.Pressured["@value"] || data.Pressured["@value"] === "false") &&
      (!data.Tangential["@value"] || data.Tangential["@value"] === "false") &&
      (!data.Incoherent["@value"] || data.Incoherent["@value"] === "false") &&
      (!data.Stutters["@value"] || data.Stutters["@value"] === "false") &&
      (!data.OtherSpeech["@value"] || data.OtherSpeech["@value"] === "false")
    ) {
      if (errs) errs.MSSpeech = "At least one value is required.";
      else
        errs = {
          MSSpeech: "At least one value is required.",
        };
    }

    if (
      (!data.Euthymic["@value"] || data.Euthymic["@value"] === "false") &&
      (!data.Anxious["@value"] || data.Anxious["@value"] === "false") &&
      (!data.Angry["@value"] || data.Angry["@value"] === "false") &&
      (!data.Depressed["@value"] || data.Depressed["@value"] === "false") &&
      (!data.Euphoric["@value"] || data.Euphoric["@value"] === "false") &&
      (!data.Irritable["@value"] || data.Irritable["@value"] === "false") &&
      (!data.Fearful["@value"] || data.Fearful["@value"] === "false") &&
      (!data.OtherMood["@value"] || data.OtherMood["@value"] === "false")
    ) {
      if (errs) errs.MSMood = "At least one value is required.";
      else
        errs = {
          MSMood: "At least one value is required.",
        };
    }

    if (
      (!data.FullRange["@value"] || data.FullRange["@value"] === "false") &&
      (!data.Restricted["@value"] || data.Restricted["@value"] === "false") &&
      (!data.Blunted["@value"] || data.Blunted["@value"] === "false") &&
      (!data.Flat["@value"] || data.Flat["@value"] === "false") &&
      (!data.Inappropriate["@value"] ||
        data.Inappropriate["@value"] === "false") &&
      (!data.OtherAffect["@value"] || data.OtherAffect["@value"] === "false")
    ) {
      if (errs) errs.MSAffect = "At least one value is required.";
      else
        errs = {
          MSAffect: "At least one value is required.",
        };
    }

    if (
      (!data.Logical["@value"] || data.Logical["@value"] === "false") &&
      (!data.Preoccupations["@value"] ||
        data.Preoccupations["@value"] === "false") &&
      (!data.Delusions["@value"] || data.Delusions["@value"] === "false") &&
      (!data.Hallucinations["@value"] ||
        data.Hallucinations["@value"] === "false") &&
      (!data.Racing["@value"] || data.Racing["@value"] === "false") &&
      (!data.Disorganized["@value"] ||
        data.Disorganized["@value"] === "false") &&
      (!data.Concrete["@value"] || data.Concrete["@value"] === "false") &&
      (!data.SuicidalIdeation["@value"] ||
        data.SuicidalIdeation["@value"] === "false") &&
      (!data.HomicidalIdeation["@value"] ||
        data.HomicidalIdeation["@value"] === "false") &&
      (!data.OtherPerception["@value"] ||
        data.OtherPerception["@value"] === "false")
    ) {
      if (errs) errs.MSPerception = "At least one value is required.";
      else
        errs = {
          MSPerception: "At least one value is required.",
        };
    }

    if (
      (!data.NoImpairment["@value"] ||
        data.NoImpairment["@value"] === "false") &&
      (!data.ShortAttentionSpan["@value"] ||
        data.ShortAttentionSpan["@value"] === "false") &&
      (!data.DifficultyConcentrating["@value"] ||
        data.DifficultyConcentrating["@value"] === "false") &&
      (!data.Distractibility["@value"] ||
        data.Distractibility["@value"] === "false") &&
      (!data.MemoryImpairment["@value"] ||
        data.MemoryImpairment["@value"] === "false") &&
      (!data.LowIntelligence["@value"] ||
        data.LowIntelligence["@value"] === "false") &&
      (!data.OtherCognition["@value"] ||
        data.OtherCognition["@value"] === "false")
    ) {
      if (errs) errs.MSCognition = "At least one value is required.";
      else
        errs = {
          MSCognition: "At least one value is required.",
        };
    }

    if (
      (!data.AppropriateInsight["@value"] ||
        data.AppropriateInsight["@value"] === "false") &&
      (!data.Fair["@value"] || data.Fair["@value"] === "false") &&
      (!data.Impaired["@value"] || data.Impaired["@value"] === "false") &&
      (!data.OtherInsight["@value"] || data.OtherInsight["@value"] === "false")
    ) {
      if (errs) errs.MSInsight = "At least one value is required.";
      else
        errs = {
          MSInsight: "At least one value is required.",
        };
    }

    return errs;
  };

  const sectionsToNavigate = [
    {
      _id: "FAMILY HISTORY",
      name: "MODULE 1: DEVELOPMENTAL HISTORY AND THERAPEUTIC WEB",
      parent: "FamilyInformation",
    },
    { _id: "History", name: "MODULE 2: MENTAL HEALTH/SUBSTANCE ABUSE HISTORY" },
    { _id: "Functioning", name: "MODULE 3: CURRENT FUNCTIONING" },
    {
      _id: "ClinicalSummary",
      name: "MODULE 4: DIAGNOSIS AND CLINICAL SUMMARY",
    },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={(e) => {
            preSignForm();
            toggleSignatureModal(e);
          }}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          customValidate={customValidate}
          sectionsToNavigate={sectionsToNavigate}
          beforePrintFunction={(callback) => {
            handleExpandStatusChange(true, callback);
          }}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <Input
                    name="PreferredName"
                    label="Child's Preferred Name:"
                    error={errors.PreferredName}
                    value={data.PreferredName["@value"]}
                    onChange={handleChange}
                    identical={compare("PreferredName")}
                    comments={comments && parsedComments}
                    addComment={() => setShowComment(true)}
                    editComment={toggleCommentModal}
                    deleteComment={handleDeleteComment}
                    acknowledgeComment={handleAcknowledgeComment}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Select
                    name="Pronouns"
                    label="Child's Pronouns:"
                    error={errors.Pronouns}
                    value={data.Pronouns["@value"]}
                    options={pronounsOptions}
                    onChange={handleChange}
                    identical={compare("Pronouns")}
                    comments={comments && parsedComments}
                    addComment={() => setShowComment(true)}
                    editComment={toggleCommentModal}
                    deleteComment={handleDeleteComment}
                    acknowledgeComment={handleAcknowledgeComment}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              {data.Pronouns["@value"] === "Describe" ? (
                <div className="row">
                  <div className="col"></div>
                  <div className="col">
                    <Input
                      name="PronounsDescription"
                      label="Please describe:"
                      error={errors.PronounsDescription}
                      value={data.PronounsDescription["@value"]}
                      onChange={handleChange}
                      identical={compare("PronounsDescription")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              ) : null}
              <div className="row">
                <div className="col">
                  <Select
                    name="IdentifiedRace"
                    label="Child's Identified Race:"
                    error={errors.IdentifiedRace}
                    value={data.IdentifiedRace["@value"]}
                    options={identifiedRaceOptions}
                    onChange={handleChange}
                    identical={compare("IdentifiedRace")}
                    comments={comments && parsedComments}
                    addComment={() => setShowComment(true)}
                    editComment={toggleCommentModal}
                    deleteComment={handleDeleteComment}
                    acknowledgeComment={handleAcknowledgeComment}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  {data.IdentifiedRace["@value"] === "Multi-cultural" ||
                  data.IdentifiedRace["@value"] === "Other" ? (
                    <Input
                      name="IdentifiedRaceDescription"
                      label="Please describe:"
                      error={errors.IdentifiedRaceDescription}
                      value={data.IdentifiedRaceDescription["@value"]}
                      onChange={handleChange}
                      identical={compare("IdentifiedRaceDescription")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  ) : null}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="DateCompleted"
                    label="Date Completed:"
                    type="date"
                    error={errors.DateCompleted}
                    value={data.DateCompleted["@value"]}
                    onChange={handleChange}
                    identical={compare("DateCompleted")}
                    comments={comments && parsedComments}
                    addComment={() => setShowComment(true)}
                    editComment={toggleCommentModal}
                    deleteComment={handleDeleteComment}
                    acknowledgeComment={handleAcknowledgeComment}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="Assessor"
                    label="Assessing Professional/Title:"
                    error={errors.Assessor}
                    value={data.Assessor["@value"]}
                    onChange={handleChange}
                    identical={compare("Assessor")}
                    comments={comments && parsedComments}
                    addComment={() => setShowComment(true)}
                    editComment={toggleCommentModal}
                    deleteComment={handleDeleteComment}
                    acknowledgeComment={handleAcknowledgeComment}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <RadioButton
                    name="PSDType"
                    label="Type of PSD:"
                    error={errors.PSDType}
                    value={data.PSDType["@value"]}
                    options={psdTypeOptions}
                    onChange={handleChange}
                    comments={comments && parsedComments}
                    addComment={() => setShowComment(true)}
                    editComment={toggleCommentModal}
                    deleteComment={handleDeleteComment}
                    acknowledgeComment={handleAcknowledgeComment}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  {data.PSDType["@value"] === "Review" ? (
                    <Input
                      name="ReviewReason"
                      label="Reason:"
                      error={errors.ReviewReason}
                      value={getFieldValue(data.ReviewReason)}
                      onChange={handleChange}
                      identical={compare("ReviewReason")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  ) : null}
                </div>
              </div>
              <h5>Information Sources:</h5>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ISClient"
                      label="Client"
                      checked={data.ISClient["@value"] === "true"}
                      onChange={handleChange}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISParent"
                      label="Parent"
                      checked={data.ISParent["@value"] === "true"}
                      onChange={handleChange}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISFamily"
                      label="Family"
                      checked={data.ISFamily["@value"] === "true"}
                      onChange={handleChange}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISGuardian"
                      label="Guardian"
                      checked={data.ISGuardian["@value"] === "true"}
                      onChange={handleChange}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ISCaregiver"
                      label="Non-parent Caregiver"
                      checked={data.ISCaregiver["@value"] === "true"}
                      onChange={handleChange}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISPhysician"
                      label="Physician"
                      checked={data.ISPhysician["@value"] === "true"}
                      onChange={handleChange}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISRecords"
                      label="Records"
                      checked={data.ISRecords["@value"] === "true"}
                      onChange={handleChange}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISServiceProvider"
                      label="Service Provider"
                      checked={data.ISServiceProvider["@value"] === "true"}
                      onChange={handleChange}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ISSchool"
                      label="School"
                      checked={data.ISSchool["@value"] === "true"}
                      onChange={handleChange}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISCourt"
                      label="Court"
                      checked={data.ISCourt["@value"] === "true"}
                      onChange={handleChange}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISDFS"
                      label="DFS"
                      checked={data.ISDFS["@value"] === "true"}
                      onChange={handleChange}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISOther"
                      label="Other"
                      checked={data.ISOther["@value"] === "true"}
                      onChange={handleChange}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              {errors.InformationSources !== undefined ? (
                <div className="row">
                  <div className="col">
                    <div className="alert alert-danger">
                      {errors.InformationSources}
                    </div>
                  </div>
                </div>
              ) : null}
              {data.ISParent["@value"] === "true" ? (
                <Input
                  name="ISParentDetails"
                  label="Parent source"
                  error={errors.ISParentDetails}
                  value={data.ISParentDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISParentDetails")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISFamily["@value"] === "true" ? (
                <Input
                  name="ISFamilyDetails"
                  label="Family source"
                  error={errors.ISFamilyDetails}
                  value={data.ISFamilyDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISFamilyDetails")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISGuardian["@value"] === "true" ? (
                <Input
                  name="ISGuardianDetails"
                  label="Guardian source"
                  error={errors.ISGuardianDetails}
                  value={data.ISGuardianDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISGuardianDetails")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISCaregiver["@value"] === "true" ? (
                <Input
                  name="ISCaregiverDetails"
                  label="Non-parent Caregiver source"
                  error={errors.ISCaregiverDetails}
                  value={data.ISCaregiverDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISCaregiverDetails")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISPhysician["@value"] === "true" ? (
                <Input
                  name="ISPhysicianDetails"
                  label="Physician source"
                  error={errors.ISPhysicianDetails}
                  value={data.ISPhysicianDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISPhysicianDetails")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISRecords["@value"] === "true" ? (
                <Input
                  name="ISRecordsDetails"
                  label="Records source"
                  error={errors.ISRecordsDetails}
                  value={data.ISRecordsDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISRecordsDetails")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISServiceProvider["@value"] === "true" ? (
                <Input
                  name="ISServiceProviderDetails"
                  label="Service Provider source"
                  error={errors.ISServiceProviderDetails}
                  value={data.ISServiceProviderDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISServiceProviderDetails")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISSchool["@value"] === "true" ? (
                <Input
                  name="ISSchoolDetails"
                  label="School source"
                  error={errors.ISSchoolDetails}
                  value={data.ISSchoolDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISSchoolDetails")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISCourt["@value"] === "true" ? (
                <Input
                  name="ISCourtDetails"
                  label="Court source"
                  error={errors.ISCourtDetails}
                  value={data.ISCourtDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISCourtDetails")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISDFS["@value"] === "true" ? (
                <Input
                  name="ISDFSDetails"
                  label="DFS source"
                  error={errors.ISDFSDetails}
                  value={data.ISDFSDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISDFSDetails")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISOther["@value"] === "true" ? (
                <Input
                  name="ISOtherDetails"
                  label="Other source"
                  error={errors.ISOtherDetails}
                  value={data.ISOtherDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISOtherDetails")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
              ) : null}
              <div className="clearfix">
                <div className="float-start">
                  <button
                    className="btn btn-primary mb-2"
                    onClick={(e) => {
                      handleExpandStatusChange();
                    }}
                  >
                    {expandStatus === true ? "Collapse All" : "Expand All"}
                  </button>
                </div>
                <div className="float-end d-flex">
                  <div className="me-2">Clinical Narrative Summary:</div>
                  <ToggleSwitch
                    values={["Show", "Hide"]}
                    selected={showNarrative}
                    onChange={setShowNarrative}
                  />
                </div>
              </div>
              <ExpandDiv
                id="FamilyInformation"
                expandStatus={expandStatus}
                className="mt-2"
                title="MODULE 1: DEVELOPMENTAL HISTORY AND THERAPEUTIC WEB"
              >
                <h5>
                  SUPPORTIVE RELATIONSHIPS (
                  <i>Family, school, community, etc.</i>
                  ):
                </h5>
                <FieldTable
                  name="FamilyInformationDetails"
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                >
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col py-2 border-end">Name</div>
                      <div className="col py-2 border-end">Relation</div>
                      <div className="col py-2">Quality</div>
                    </div>
                    {data.FamilyInformationDetails["@value"] &&
                      JSON.parse(data.FamilyInformationDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col py-2 border-end d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "FamilyInformationDetails"
                                      );
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "FamilyInformationDetails"
                                      );
                                    }}
                                  ></i>
                                  <Input
                                    name="Name"
                                    value={item.Name}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "FamilyInformationDetails"
                                      );
                                    }}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "FamilyInformationDetails"
                                      );
                                    }}
                                  ></i>
                                  {item.Name}
                                </>
                              )}
                            </div>
                            <div className="col py-2 border-end">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Relation"
                                  value={item.Relation}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "FamilyInformationDetails"
                                    );
                                  }}
                                />
                              ) : (
                                item.Relation
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Select
                                  name="Quality"
                                  value={item.Quality}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "FamilyInformationDetails"
                                    );
                                  }}
                                  options={qualityOptions}
                                />
                              ) : (
                                item.Quality
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {formsyDetails.Active === "1" &&
                    formsyDetails.FinalSigned !== "1" && (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("FamilyInformationDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Individual
                      </button>
                    )}
                  {errors.FamilyInformationDetails && (
                    <div className="alert alert-danger">
                      {errors.FamilyInformationDetails}
                    </div>
                  )}
                </FieldTable>

                <h5>CURRENT HOME ENVIRONMENT</h5>
                <ol>
                  <li>
                    With whom do you currently live (
                    <i>
                      Include all people and animals who live in the current
                      home along with ages and sleeping arrangements; note
                      whether placed with biological siblings
                    </i>
                    )?
                    <Input
                      name="CurrentlyLiveWith"
                      error={errors.CurrentlyLiveWith}
                      value={data.CurrentlyLiveWith["@value"]}
                      onChange={handleChange}
                      identical={compare("CurrentlyLiveWith")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  <li>
                    Do you have contact with biological family (
                    <i>
                      Include information surrounding frequency and consistency
                      of phone calls and visits, with whom contact is permitted,
                      as well as any restrictions surrounding contact
                    </i>
                    )?
                    <Input
                      name="ContactWithBioFamily"
                      error={errors.ContactWithBioFamily}
                      value={data.ContactWithBioFamily["@value"]}
                      onChange={handleChange}
                      identical={compare("ContactWithBioFamily")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  <li>
                    Describe the dynamics and interactions within these
                    relationships (
                    <i>
                      Include intimacy and comfort, predictability, security of
                      attachment, fear of rejection, need for
                      control/parentification, stressors and strengths within
                      each relationship
                    </i>
                    ):
                    <Input
                      name="RelationshipDynamics"
                      error={errors.RelationshipDynamics}
                      value={data.RelationshipDynamics["@value"]}
                      onChange={handleChange}
                      identical={compare("RelationshipDynamics")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  <li>
                    Parenting Style and Philosophy (
                    <i>
                      Describe consistency, predictability, flexibility, amount
                      of control provided to children to make choices,
                      information provided to children surrounding what to
                      expect, attunement to individual members, presence, mood,
                      respect, etc. as influenced by caregivers' own upbringing
                    </i>
                    ):
                    <Input
                      name="ParentingStyle"
                      error={errors.ParentingStyle}
                      value={data.ParentingStyle["@value"]}
                      onChange={handleChange}
                      identical={compare("ParentingStyle")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  <li>
                    Cultural Considerations (
                    <i>
                      Note whether current caregivers and child share similar
                      ethnic, spiritual, personal and/or other diverse
                      background
                    </i>
                    ):
                    <Input
                      name="CulturalConsiderations"
                      error={errors.CulturalConsiderations}
                      value={data.CulturalConsiderations["@value"]}
                      onChange={handleChange}
                      identical={compare("CulturalConsiderations")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                </ol>
                <br />
                <h5>BIOLOGICAL FAMILY INFORMATION</h5>
                <FieldTable
                  name="BioFamilyInfo"
                  commentsHandler={handleComments}
                >
                  <div className="container border-top border-start border-end mb-2">
                    <div className="row border-bottom">
                      <div className="col border-end py-2">Name</div>
                      <div className="col border-end py-2">Age</div>
                      <div className="col border-end py-2">Relation</div>
                      <div className="col py-2">Quality</div>
                    </div>
                    {data.BioFamilyInfo["@value"] &&
                      JSON.parse(data.BioFamilyInfo["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(index, "BioFamilyInfo");
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(index, "BioFamilyInfo");
                                    }}
                                  ></i>
                                  <Input
                                    name="Name"
                                    value={item.Name}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "BioFamilyInfo"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(index, "BioFamilyInfo");
                                    }}
                                  ></i>
                                  {item.Name}
                                </>
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Age"
                                  value={item.Age}
                                  onChange={(e) => {
                                    handleRowChange(e, index, "BioFamilyInfo");
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Age
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Relation"
                                  value={item.Relation}
                                  onChange={(e) => {
                                    handleRowChange(e, index, "BioFamilyInfo");
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Relation
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Select
                                  name="Quality"
                                  value={item.Quality}
                                  onChange={(e) => {
                                    handleRowChange(e, index, "BioFamilyInfo");
                                  }}
                                  disabled={formDisabled}
                                  options={qualityOptions}
                                />
                              ) : (
                                item.Quality
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {formsyDetails.Active === "1" &&
                    formsyDetails.FinalSigned !== "1" && (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("BioFamilyInfo", { IsEditable: true });
                        }}
                      >
                        Add Family Member
                      </button>
                    )}
                  {errors.BioFamilyInfo && (
                    <div className="alert alert-danger">
                      {errors.BioFamilyInfo}
                    </div>
                  )}
                </FieldTable>
                <h5 id="FAMILY HISTORY">FAMILY HISTORY</h5>
                <ol>
                  <li>
                    Family Strengths - resiliency factors and accomplishments:
                    <Input
                      name="FamilyStrengths"
                      error={errors.FamilyStrengths}
                      value={data.FamilyStrengths["@value"]}
                      onChange={handleChange}
                      identical={compare("FamilyStrengths")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  <li>
                    Genetic Factors (
                    <i>
                      Include known or suspected genetic problems in the child
                      or child's family, such as Huntington's; Fragile X; Downs
                      Syndrome; Bipolar; Schizophrenia; Autism Spectrum
                      Disorders; etc.
                    </i>
                    ):
                    <Input
                      name="GeneticFactors"
                      error={errors.GeneticFactors}
                      value={data.GeneticFactors["@value"]}
                      onChange={handleChange}
                      identical={compare("GeneticFactors")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  <li>
                    Epigenetic Factors (
                    <i>
                      Factors in the life of the mother and father prior to
                      conception which have the ability to influence chromosomal
                      proteins and therefore can impact the genetic expression
                      in their child, such as any adverse childhood experiences
                    </i>
                    ):
                    <Input
                      name="EpigeneticFactors"
                      error={errors.EpigeneticFactors}
                      value={data.EpigeneticFactors["@value"]}
                      onChange={handleChange}
                      identical={compare("EpigeneticFactors")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  <li>
                    Parent Relationship History (
                    <i>
                      Narrative of how the relationship developed, maintained
                      and/or ended to include whether the pregnancy was
                      planned/wanted; did the father support the mother; what
                      was the quality of the relationship over time; etc.
                    </i>
                    ):
                    <Input
                      name="RelationshipHistory"
                      error={errors.RelationshipHistory}
                      value={data.RelationshipHistory["@value"]}
                      onChange={handleChange}
                      identical={compare("RelationshipHistory")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                </ol>
                <h5>HISTORY - SIGNIFICANT STRESSFUL LIFE EVENTS</h5>
                <ol>
                  <li>
                    CPS History (
                    <i>
                      Include both substantiated and unsubstantiated concerns
                      along with dates
                    </i>
                    ):
                    <Input
                      name="CPSHistory"
                      error={errors.CPSHistory}
                      value={data.CPSHistory["@value"]}
                      onChange={handleChange}
                      identical={compare("CPSHistory")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  <li>
                    <FieldTable
                      name="PlacementHistory"
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                    >
                      Placement History
                      <div className="container border mb-2">
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            Dates of Placement
                          </div>
                          <div className="col py-2">Type of Placement</div>
                        </div>
                        {data.PlacementHistory["@value"]
                          ? JSON.parse(data.PlacementHistory["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col py-2 border-end d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "PlacementHistory"
                                            );
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "PlacementHistory"
                                            );
                                          }}
                                        ></i>
                                        <Input
                                          name="Dates"
                                          value={item.Dates}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "PlacementHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "PlacementHistory"
                                            );
                                          }}
                                        ></i>
                                        {item.Dates}
                                      </>
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <div className="container">
                                          <div className="row">
                                            <div className="col">
                                              <Checkbox
                                                name={`TFC-${index}`}
                                                label="Treatment Foster Care"
                                                checked={item.TFC === "true"}
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "PlacementHistory"
                                                  );
                                                }}
                                                disabled={
                                                  formsyDetails.Active ===
                                                    "0" ||
                                                  formsyDetails.FinalSigned ===
                                                    "1"
                                                }
                                              />
                                            </div>
                                            <div className="col">
                                              <Checkbox
                                                name={`Kinship-${index}`}
                                                label="Kinship"
                                                checked={
                                                  item.Kinship === "true"
                                                }
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "PlacementHistory"
                                                  );
                                                }}
                                                disabled={
                                                  formsyDetails.Active ===
                                                    "0" ||
                                                  formsyDetails.FinalSigned ===
                                                    "1"
                                                }
                                              />
                                            </div>
                                            <div className="col">
                                              <Checkbox
                                                name={`DFS-${index}`}
                                                label="DFS"
                                                checked={item.DFS === "true"}
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "PlacementHistory"
                                                  );
                                                }}
                                                disabled={
                                                  formsyDetails.Active ===
                                                    "0" ||
                                                  formsyDetails.FinalSigned ===
                                                    "1"
                                                }
                                              />
                                            </div>
                                          </div>
                                          <div className="row">
                                            <div className="col">
                                              <Checkbox
                                                name={`Hospital-${index}`}
                                                label="Hospital"
                                                checked={
                                                  item.Hospital === "true"
                                                }
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "PlacementHistory"
                                                  );
                                                }}
                                                disabled={
                                                  formsyDetails.Active ===
                                                    "0" ||
                                                  formsyDetails.FinalSigned ===
                                                    "1"
                                                }
                                              />
                                            </div>
                                            <div className="col">
                                              <Checkbox
                                                name={`EmergencyShelter-${index}`}
                                                label="Emergency Shelter"
                                                checked={
                                                  item.EmergencyShelter ===
                                                  "true"
                                                }
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "PlacementHistory"
                                                  );
                                                }}
                                                disabled={
                                                  formsyDetails.Active ===
                                                    "0" ||
                                                  formsyDetails.FinalSigned ===
                                                    "1"
                                                }
                                              />
                                            </div>
                                            <div className="col">
                                              <Checkbox
                                                name={`BiologicalHome-${index}`}
                                                label="Biological Home"
                                                checked={
                                                  item.BiologicalHome === "true"
                                                }
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "PlacementHistory"
                                                  );
                                                }}
                                                disabled={
                                                  formsyDetails.Active ===
                                                    "0" ||
                                                  formsyDetails.FinalSigned ===
                                                    "1"
                                                }
                                              />
                                            </div>
                                          </div>
                                          <div className="row">
                                            <div className="col">
                                              <Checkbox
                                                name={`ResidentialTreatmentCenter-${index}`}
                                                label="Residential Treatment Center"
                                                checked={
                                                  item.ResidentialTreatmentCenter ===
                                                  "true"
                                                }
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "PlacementHistory"
                                                  );
                                                }}
                                                disabled={
                                                  formsyDetails.Active ===
                                                    "0" ||
                                                  formsyDetails.FinalSigned ===
                                                    "1"
                                                }
                                              />
                                            </div>
                                            <div className="col">
                                              <Checkbox
                                                name={`Other-${index}`}
                                                label="Other"
                                                checked={item.Other === "true"}
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "PlacementHistory"
                                                  );
                                                }}
                                                disabled={
                                                  formsyDetails.Active ===
                                                    "0" ||
                                                  formsyDetails.FinalSigned ===
                                                    "1"
                                                }
                                              />
                                            </div>
                                          </div>
                                        </div>
                                        {item.Other === "true" ? (
                                          <Input
                                            name="OtherDetails"
                                            value={item.OtherDetails}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "PlacementHistory"
                                              );
                                            }}
                                          />
                                        ) : null}
                                      </>
                                    ) : (
                                      getPlacementType(item)
                                    )}
                                  </div>
                                </div>
                              )
                            )
                          : null}
                      </div>
                      {formsyDetails.Active === "1" &&
                        formsyDetails.FinalSigned !== "1" && (
                          <>
                            <button
                              className="btn btn-primary mb-2"
                              onClick={() => {
                                handleAddRow("PlacementHistory", {
                                  IsEditable: true,
                                });
                              }}
                            >
                              Add Placement
                            </button>
                          </>
                        )}
                      {errors.PlacementHistory !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.PlacementHistory}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </FieldTable>
                  </li>
                  <li>
                    Juvenile Justice History (
                    <i>
                      Include information related to circumstance of the event
                      that resulted in a charge, the charge(s), probation and
                      court date information, assigned officer, outcomes, etc.
                    </i>
                    ):
                    <Input
                      name="JuvenileJusticeHistory"
                      error={errors.JuvenileJusticeHistory}
                      value={data.JuvenileJusticeHistory["@value"]}
                      onChange={handleChange}
                      identical={compare("JuvenileJusticeHistory")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  <li>
                    Permanency/Adoption History (
                    <i>
                      Include dates and outcomes of court processes, such as TPR
                    </i>
                    ):
                    <Input
                      name="AdoptionHistory"
                      error={errors.AdoptionHistory}
                      value={data.AdoptionHistory["@value"]}
                      onChange={handleChange}
                      identical={compare("AdoptionHistory")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                </ol>
                <div className="container border mb-2">
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      Adverse Childhood Experience
                    </div>
                    <div className="col py-2 border-end">Status</div>
                    <div className="col py-2">
                      Comments (age, known triggers, etc.)
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">Physical Abuse</div>
                    <div className="col py-2 border-end">
                      <RadioButton
                        name="PhysicalAbuse"
                        options={yesNoSusOptions}
                        error={errors.PhysicalAbuse}
                        value={data.PhysicalAbuse["@value"]}
                        onChange={handleChange}
                        identical={compare("PhysicalAbuse")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col py-2">
                      {data.PhysicalAbuse["@value"] === "Yes" ||
                      data.PhysicalAbuse["@value"] === "Suspected" ? (
                        <Input
                          name="PhysicalAbuseComments"
                          error={errors.PhysicalAbuseComments}
                          value={data.PhysicalAbuseComments["@value"]}
                          onChange={handleChange}
                          identical={compare("PhysicalAbuseComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">Sexual Abuse</div>
                    <div className="col py-2 border-end">
                      <RadioButton
                        name="SexualAbuse"
                        options={yesNoSusOptions}
                        error={errors.SexualAbuse}
                        value={data.SexualAbuse["@value"]}
                        onChange={handleChange}
                        identical={compare("SexualAbuse")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col py-2">
                      {data.SexualAbuse["@value"] === "Yes" ||
                      data.SexualAbuse["@value"] === "Suspected" ? (
                        <Input
                          name="SexualAbuseComments"
                          error={errors.SexualAbuseComments}
                          value={data.SexualAbuseComments["@value"]}
                          onChange={handleChange}
                          identical={compare("SexualAbuseComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">Emotional Abuse</div>
                    <div className="col py-2 border-end">
                      <RadioButton
                        name="EmotionalAbuse"
                        options={yesNoSusOptions}
                        error={errors.EmotionalAbuse}
                        value={data.EmotionalAbuse["@value"]}
                        onChange={handleChange}
                        identical={compare("EmotionalAbuse")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col py-2">
                      {data.EmotionalAbuse["@value"] === "Yes" ||
                      data.EmotionalAbuse["@value"] === "Suspected" ? (
                        <Input
                          name="EmotionalAbuseComments"
                          error={errors.EmotionalAbuseComments}
                          value={data.EmotionalAbuseComments["@value"]}
                          onChange={handleChange}
                          identical={compare("EmotionalAbuseComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">Physical Neglect</div>
                    <div className="col py-2 border-end">
                      <RadioButton
                        name="PhysicalNeglect"
                        options={yesNoSusOptions}
                        error={errors.PhysicalNeglect}
                        value={data.PhysicalNeglect["@value"]}
                        onChange={handleChange}
                        identical={compare("PhysicalNeglect")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col py-2">
                      {data.PhysicalNeglect["@value"] === "Yes" ||
                      data.PhysicalNeglect["@value"] === "Suspected" ? (
                        <Input
                          name="PhysicalNeglectComments"
                          error={errors.PhysicalNeglectComments}
                          value={data.PhysicalNeglectComments["@value"]}
                          onChange={handleChange}
                          identical={compare("PhysicalNeglectComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">Emotional Neglect</div>
                    <div className="col py-2 border-end">
                      <RadioButton
                        name="EmotionalNeglect"
                        options={yesNoSusOptions}
                        error={errors.EmotionalNeglect}
                        value={data.EmotionalNeglect["@value"]}
                        onChange={handleChange}
                        identical={compare("EmotionalNeglect")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col py-2">
                      {data.EmotionalNeglect["@value"] === "Yes" ||
                      data.EmotionalNeglect["@value"] === "Suspected" ? (
                        <Input
                          name="EmotionalNeglectComments"
                          error={errors.EmotionalNeglectComments}
                          value={data.EmotionalNeglectComments["@value"]}
                          onChange={handleChange}
                          identical={compare("EmotionalNeglectComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">Mental Illness</div>
                    <div className="col py-2 border-end">
                      <RadioButton
                        name="MentalIllness"
                        options={yesNoSusOptions}
                        error={errors.MentalIllness}
                        value={data.MentalIllness["@value"]}
                        onChange={handleChange}
                        identical={compare("MentalIllness")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col py-2">
                      {data.MentalIllness["@value"] === "Yes" ||
                      data.MentalIllness["@value"] === "Suspected" ? (
                        <Input
                          name="MentalIllnessComments"
                          error={errors.MentalIllnessComments}
                          value={data.MentalIllnessComments["@value"]}
                          onChange={handleChange}
                          identical={compare("MentalIllnessComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">Divorce</div>
                    <div className="col py-2 border-end">
                      <RadioButton
                        name="Divorce"
                        options={yesNoSusOptions}
                        error={errors.Divorce}
                        value={data.Divorce["@value"]}
                        onChange={handleChange}
                        identical={compare("Divorce")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col py-2">
                      {data.Divorce["@value"] === "Yes" ||
                      data.Divorce["@value"] === "Suspected" ? (
                        <Input
                          name="DivorceComments"
                          error={errors.DivorceComments}
                          value={data.DivorceComments["@value"]}
                          onChange={handleChange}
                          identical={compare("DivorceComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">Substance Abuse</div>
                    <div className="col py-2 border-end">
                      <RadioButton
                        name="SubstanceAbuse"
                        options={yesNoSusOptions}
                        error={errors.SubstanceAbuse}
                        value={data.SubstanceAbuse["@value"]}
                        onChange={handleChange}
                        identical={compare("SubstanceAbuse")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col py-2">
                      {data.SubstanceAbuse["@value"] === "Yes" ||
                      data.SubstanceAbuse["@value"] === "Suspected" ? (
                        <Input
                          name="SubstanceAbuseComments"
                          error={errors.SubstanceAbuseComments}
                          value={data.SubstanceAbuseComments["@value"]}
                          onChange={handleChange}
                          identical={compare("SubstanceAbuseComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      Violence Against Your Mother
                    </div>
                    <div className="col py-2 border-end">
                      <RadioButton
                        name="ViolenceAgainstMom"
                        options={yesNoSusOptions}
                        error={errors.ViolenceAgainstMom}
                        value={data.ViolenceAgainstMom["@value"]}
                        onChange={handleChange}
                        identical={compare("ViolenceAgainstMom")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col py-2">
                      {data.ViolenceAgainstMom["@value"] === "Yes" ||
                      data.ViolenceAgainstMom["@value"] === "Suspected" ? (
                        <Input
                          name="ViolenceAgainstMomComments"
                          error={errors.ViolenceAgainstMomComments}
                          value={data.ViolenceAgainstMomComments["@value"]}
                          onChange={handleChange}
                          identical={compare("ViolenceAgainstMomComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      Mental Illness of primary caregivers
                    </div>
                    <div className="col py-2 border-end">
                      <RadioButton
                        name="CaregiverMentalIllness"
                        options={yesNoSusOptions}
                        error={errors.CaregiverMentalIllness}
                        value={data.CaregiverMentalIllness["@value"]}
                        onChange={handleChange}
                        identical={compare("CaregiverMentalIllness")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col py-2">
                      {data.CaregiverMentalIllness["@value"] === "Yes" ||
                      data.CaregiverMentalIllness["@value"] === "Suspected" ? (
                        <Input
                          name="CaregiverMentalIllnessComments"
                          error={errors.CaregiverMentalIllnessComments}
                          value={data.CaregiverMentalIllnessComments["@value"]}
                          onChange={handleChange}
                          identical={compare("CaregiverMentalIllnessComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col py-2 border-end">
                      Having a Relative Who Has Been to Jail/Prison
                    </div>
                    <div className="col py-2 border-end">
                      <RadioButton
                        name="RelativeJail"
                        options={yesNoSusOptions}
                        error={errors.RelativeJail}
                        value={data.RelativeJail["@value"]}
                        onChange={handleChange}
                        identical={compare("RelativeJail")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col py-2">
                      {data.RelativeJail["@value"] === "Yes" ||
                      data.RelativeJail["@value"] === "Suspected" ? (
                        <Input
                          name="RelativeJailComments"
                          error={errors.RelativeJailComments}
                          value={data.RelativeJailComments["@value"]}
                          onChange={handleChange}
                          identical={compare("RelativeJailComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
                <h5>DEVELOPMENTAL INFORMATION</h5>
                <RadioButton
                  name="AgeCategory"
                  label="What is the youth's age category?"
                  options={ageCategoryOptions}
                  error={errors.AgeCategory}
                  value={data.AgeCategory["@value"]}
                  onChange={handleChange}
                  identical={compare("AgeCategory")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
                <ol>
                  <li>
                    Intrauterine Experiences (
                    <i>
                      Consider supports and stressors related to social,
                      emotional, medical, residential, financial, substance use,
                      and physical safety
                    </i>
                    ):
                    <Input
                      name="IntrauterineExperiences"
                      error={errors.IntrauterineExperiences}
                      value={data.IntrauterineExperiences["@value"]}
                      onChange={handleChange}
                      identical={compare("IntrauterineExperiences")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  <li>
                    Birth Experiences (
                    <i>
                      Consider age of mother, length of pregnancy,
                      complications, etc.
                    </i>
                    ):
                    <Input
                      name="BirthExperiences"
                      error={errors.BirthExperiences}
                      value={data.BirthExperiences["@value"]}
                      onChange={handleChange}
                      identical={compare("BirthExperiences")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  <li>
                    Perinatal Experiences (
                    <i>
                      Consider supports and stressors related to social,
                      emotional, medical, caregiving, siblings, residential,
                      financial, substance use and physical safety during the
                      first 2 months of life
                    </i>
                    ):
                    <Input
                      name="PerinatalExperiences"
                      error={errors.PerinatalExperiences}
                      value={data.PerinatalExperiences["@value"]}
                      onChange={handleChange}
                      identical={compare("PerinatalExperiences")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  <li>
                    Infancy Experiences (
                    <i>
                      Consider supports and stressors related to social,
                      emotional, medical, caregiving, siblings, residential,
                      financial, substance use and physical safety of the child
                      and family during the first 2 months to 1 year of life as
                      well as the child's temperament and whether the child was
                      easy/difficult to comfort, feed, or cuddle
                    </i>
                    ):
                    <Input
                      name="InfancyExperiences"
                      error={errors.InfancyExperiences}
                      value={data.InfancyExperiences["@value"]}
                      onChange={handleChange}
                      identical={compare("InfancyExperiences")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  <li>
                    Early Childhood Experiences (
                    <i>
                      Consider supports and stressors related to social,
                      emotional, medical, caregiving, siblings, residential,
                      financial, substance use and physical safety of the child
                      and family during the first year to 3 years of life
                    </i>
                    ):
                    <Input
                      name="EarlyChildhoodExperiences"
                      error={errors.EarlyChildhoodExperiences}
                      value={data.EarlyChildhoodExperiences["@value"]}
                      onChange={handleChange}
                      identical={compare("EarlyChildhoodExperiences")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                  </li>
                  {data.AgeCategory["@value"] === "4-10" ||
                  data.AgeCategory["@value"] === "11-18" ||
                  data.AgeCategory["@value"] === "19-24" ||
                  data.AgeCategory["@value"] === "25+" ? (
                    <>
                      <li>
                        Childhood Experiences (
                        <i>
                          Consider supports and stressors related to social,
                          emotional, medical, school, caregiving, siblings,
                          residential, financial, substance use and physical
                          safety of the child and family during the first 4 to
                          10 years of life
                        </i>
                        ):
                        <Input
                          name="ChildhoodExperiences"
                          error={errors.ChildhoodExperiences}
                          value={data.ChildhoodExperiences["@value"]}
                          onChange={handleChange}
                          identical={compare("ChildhoodExperiences")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      </li>
                    </>
                  ) : null}
                  {data.AgeCategory["@value"] === "11-18" ||
                  data.AgeCategory["@value"] === "19-24" ||
                  data.AgeCategory["@value"] === "25+" ? (
                    <>
                      <li>
                        Youth Experiences (
                        <i>
                          Consider supports and stressors related to social,
                          emotional, medical, school, caregiving, siblings,
                          residential, financial, substance use and physical
                          safety of the child and family during the first 11 to
                          18 years of life
                        </i>
                        ):
                        <Input
                          name="YouthExperiences"
                          error={errors.YouthExperiences}
                          value={data.YouthExperiences["@value"]}
                          onChange={handleChange}
                          identical={compare("YouthExperiences")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      </li>
                    </>
                  ) : null}
                  {data.AgeCategory["@value"] === "19-24" ||
                  data.AgeCategory["@value"] === "25+" ? (
                    <>
                      <li>
                        Early Adult Experiences (
                        <i>
                          Consider supports and stressors related to social,
                          emotional, medical, school, caregiving, siblings,
                          residential, financial, substance use and physical
                          safety of the child and family during the first 18 to
                          24 years of life
                        </i>
                        ):
                        <Input
                          name="EarlyAdultExperiences"
                          error={errors.EarlyAdultExperiences}
                          value={data.EarlyAdultExperiences["@value"]}
                          onChange={handleChange}
                          identical={compare("EarlyAdultExperiences")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      </li>
                    </>
                  ) : null}
                  {data.AgeCategory["@value"] === "25+" ? (
                    <>
                      <li>
                        Adult Experiences (
                        <i>
                          Consider supports and stressors related to social,
                          emotional, medical, school, caregiving, siblings,
                          residential, financial, substance use and physical
                          safety of the child and family during the years of
                          life beyond age 25
                        </i>
                        ):
                        <Input
                          name="AdultExperiences"
                          error={errors.AdultExperiences}
                          value={data.AdultExperiences["@value"]}
                          onChange={handleChange}
                          identical={compare("AdultExperiences")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      </li>
                    </>
                  ) : null}
                </ol>
              </ExpandDiv>
              <ExpandDiv
                id="History"
                expandStatus={expandStatus}
                className="mt-2"
                title="MODULE 2: MENTAL HEALTH/SUBSTANCE ABUSE HISTORY"
              >
                <h5>MENTAL HEALTH TREATMENT HISTORY</h5>
                <Checkbox
                  name="MentalHealthTreatmentNA"
                  label="N/A (skip section)"
                  checked={data.MentalHealthTreatmentNA["@value"] === "true"}
                  onChange={handleChange}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
                {data.MentalHealthTreatmentNA["@value"] !== "true" ? (
                  <>
                    <FieldTable
                      name="MentalHealthDiagnoses"
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                    >
                      Current and Past Diagnoses:
                      <br />
                      <div className="container border mb-2">
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">Diagnosis</div>
                          <div className="col py-2 border-end">Status</div>
                          <div className="col py-2 border-end">
                            Diagnosing Provider/Agency
                          </div>
                          <div className="col py-2">Dates</div>
                        </div>
                        {data.MentalHealthDiagnoses["@value"] &&
                          JSON.parse(data.MentalHealthDiagnoses["@value"]).map(
                            (item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col py-2 border-end d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "MentalHealthDiagnoses"
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "MentalHealthDiagnoses"
                                          );
                                        }}
                                      ></i>
                                      <Input
                                        name="Diagnosis"
                                        value={item.Diagnosis}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "MentalHealthDiagnoses"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "MentalHealthDiagnoses"
                                          );
                                        }}
                                      ></i>
                                      {item.Diagnosis}
                                    </>
                                  )}
                                </div>
                                <div className="col py-2 border-end">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <RadioButton
                                      name="Status"
                                      value={item.Status}
                                      options={
                                        mentalHealthDiagnosisStatusOptions
                                      }
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "MentalHealthDiagnoses"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Status
                                  )}
                                </div>
                                <div className="col py-2 border-end">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Provider"
                                      value={item.Provider}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "MentalHealthDiagnoses"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Provider
                                  )}
                                </div>
                                <div className="col py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Dates"
                                      value={item.Dates}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "MentalHealthDiagnoses"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Dates
                                  )}
                                </div>
                              </div>
                            )
                          )}
                      </div>
                      {formsyDetails.Active === "1" &&
                        formsyDetails.FinalSigned !== "1" && (
                          <>
                            <button
                              className="btn btn-primary mb-2"
                              onClick={() => {
                                handleAddRow("MentalHealthDiagnoses", {
                                  IsEditable: true,
                                });
                              }}
                            >
                              Add Diagnosis
                            </button>
                          </>
                        )}
                      {errors.MentalHealthDiagnoses !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.MentalHealthDiagnoses}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </FieldTable>
                    <br />
                    <br />
                    <FieldTable
                      name="MentalHealthTreatmentHistory"
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                    >
                      Past Mental Health Treatment:
                      <br />
                      <div className="container border mb-2">
                        <div className="row border-bottom">
                          <div className="col-3 py-2 border-end">
                            Agency/Facility
                          </div>
                          <div className="col-3 py-2 border-end">Dates</div>
                          <div className="col py-2">
                            Summary of Services/Treatment
                          </div>
                        </div>
                        {data.MentalHealthTreatmentHistory["@value"] &&
                          JSON.parse(
                            data.MentalHealthTreatmentHistory["@value"]
                          ).map((item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col-3 py-2 border-end d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "MentalHealthTreatmentHistory"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "MentalHealthTreatmentHistory"
                                        );
                                      }}
                                    ></i>
                                    <Input
                                      name="Agency"
                                      value={item.Agency}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "MentalHealthTreatmentHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "MentalHealthTreatmentHistory"
                                        );
                                      }}
                                    ></i>
                                    {item.Agency}
                                  </>
                                )}
                              </div>
                              <div className="col-3 py-2 border-end">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Dates"
                                    value={item.Dates}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "MentalHealthTreatmentHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Dates
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <TextArea
                                    name="Summary"
                                    value={item.Summary}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "MentalHealthTreatmentHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Summary
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                      {formsyDetails.Active === "1" &&
                      formsyDetails.FinalSigned !== "1" ? (
                        <>
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("MentalHealthTreatmentHistory", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add Agency/Facility
                          </button>
                        </>
                      ) : null}
                      {errors.MentalHealthTreatmentHistory !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.MentalHealthTreatmentHistory}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </FieldTable>
                  </>
                ) : null}
                <br />
                <br />
                <h5>SUBSTANCE ABUSE HISTORY:</h5>
                <Checkbox
                  name="SubstanceUseNA"
                  label="N/A (skip section)"
                  checked={data.SubstanceUseNA["@value"] === "true"}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  onChange={handleSubstanceUseNAChange}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
                {data.SubstanceUseNA["@value"] !== "true" ? (
                  <>
                    <Input
                      name="PastSubstanceUse"
                      label="History of past drug or alcohol use:"
                      error={errors.PastSubstanceUse}
                      value={data.PastSubstanceUse["@value"]}
                      onChange={handleChange}
                      identical={compare("PastSubstanceUse")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                    <FieldTable
                      name="SubstanceUseHistory"
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                    >
                      Current drug or alcohol use:
                      <div className="container border mb-2">
                        <div className="row border-bottom ">
                          <div className="col py-2 border-end">Substance</div>
                          <div className="col py-2 border-end">First Use</div>
                          <div className="col py-2 border-end">Last Use</div>
                          <div className="col py-2 border-end">Frequency</div>
                          <div className="col py-2 border-end">Amount</div>
                          <div className="col py-2">Method</div>
                        </div>
                        {data.SubstanceUseHistory["@value"]
                          ? JSON.parse(data.SubstanceUseHistory["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col py-2 border-end d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "SubstanceUseHistory"
                                            );
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "SubstanceUseHistory"
                                            );
                                          }}
                                        ></i>
                                        <Input
                                          name="Substance"
                                          value={item.Substance}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SubstanceUseHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "SubstanceUseHistory"
                                            );
                                          }}
                                        ></i>
                                        {item.Substance}
                                      </>
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="FirstUse"
                                        value={item.FirstUse}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.FirstUse
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="LastUse"
                                        value={item.LastUse}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.LastUse
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="Frequency"
                                        value={item.Frequency}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Frequency
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="Amount"
                                        value={item.Amount}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Amount
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="Method"
                                        value={item.Method}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Method
                                    )}
                                  </div>
                                </div>
                              )
                            )
                          : null}
                      </div>
                      {formsyDetails.Active === "1" &&
                        formsyDetails.FinalSigned !== "1" && (
                          <>
                            <button
                              className="btn btn-primary mb-2"
                              onClick={() => {
                                handleAddRow("SubstanceUseHistory", {
                                  IsEditable: true,
                                });
                              }}
                            >
                              Add Substance
                            </button>
                          </>
                        )}
                      {errors.SubstanceUseHistory !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.SubstanceUseHistory}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </FieldTable>
                    <br />
                    <br />
                    <Input
                      name="PerceptionOfSubstanceUse"
                      label="Client and caregiver perception of substance use:"
                      error={errors.PerceptionOfSubstanceUse}
                      value={data.PerceptionOfSubstanceUse["@value"]}
                      onChange={handleChange}
                      identical={compare("PerceptionOfSubstanceUse")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                    <Input
                      name="SubstanceUseComments"
                      label="Comments (including impact to current functioning):"
                      error={errors.SubstanceUseComments}
                      value={data.SubstanceUseComments["@value"]}
                      onChange={handleChange}
                      identical={compare("SubstanceUseComments")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                    <br />
                    <b>Alcohol/Drug Treatment History</b>
                    <Checkbox
                      name="NoPastTreatment"
                      label="No past treatment"
                      divClassName="ms-3"
                      checked={data.NoPastTreatment["@value"] === "true"}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    <FieldTable
                      name="SubstanceUseTreatmentHistory"
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                    >
                      {data.NoPastTreatment["@value"] !== "true" ? (
                        <>
                          <br />
                          <div className="container border mb-2">
                            <div className="row border-bottom">
                              <div className="col-3 border-end py-2">
                                Agency/Facility
                              </div>
                              <div className="col-3 border-end py-2">Dates</div>
                              <div className="col-6 py-2">
                                Summary of Services/Treatment
                              </div>
                            </div>
                            {data.SubstanceUseTreatmentHistory["@value"]
                              ? JSON.parse(
                                  data.SubstanceUseTreatmentHistory["@value"]
                                ).map((item, index) => (
                                  <div
                                    key={item._id}
                                    className="row border-bottom"
                                  >
                                    <div className="col py-2 border-end d-flex">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <>
                                          <i
                                            className="fas fa-save me-2 d-print-none"
                                            onClick={() => {
                                              handleSaveRow(
                                                index,
                                                "SubstanceUseTreatmentHistory"
                                              );
                                            }}
                                          ></i>
                                          <i
                                            className="far fa-trash-alt me-2 d-print-none"
                                            onClick={() => {
                                              handleDeleteRow(
                                                index,
                                                "SubstanceUseTreatmentHistory"
                                              );
                                            }}
                                          ></i>
                                          <Input
                                            name="Agency"
                                            value={item.Agency}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "SubstanceUseTreatmentHistory"
                                              );
                                            }}
                                            comments={
                                              comments && parsedComments
                                            }
                                            addComment={() =>
                                              setShowComment(true)
                                            }
                                            editComment={toggleCommentModal}
                                            deleteComment={handleDeleteComment}
                                            acknowledgeComment={
                                              handleAcknowledgeComment
                                            }
                                            disabled={formDisabled}
                                          />
                                        </>
                                      ) : (
                                        <>
                                          <i
                                            className="fas fa-pen me-2 d-print-none"
                                            onClick={() => {
                                              handleEditRow(
                                                index,
                                                "SubstanceUseTreatmentHistory"
                                              );
                                            }}
                                          ></i>
                                          {item.Agency}
                                        </>
                                      )}
                                    </div>
                                    <div className="col-3 border-end py-2">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <Input
                                          name="Dates"
                                          value={item.Dates}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SubstanceUseTreatmentHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : (
                                        item.Dates
                                      )}
                                    </div>
                                    <div className="col-6 py-2">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <TextArea
                                          name="Summary"
                                          value={item.Summary}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SubstanceUseTreatmentHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : (
                                        item.Summary
                                      )}
                                    </div>
                                  </div>
                                ))
                              : null}
                          </div>
                          {formsyDetails.Active === "1" &&
                            formsyDetails.FinalSigned !== "1" && (
                              <>
                                <button
                                  className="btn btn-primary mb-2"
                                  onClick={() => {
                                    handleAddRow(
                                      "SubstanceUseTreatmentHistory",
                                      { IsEditable: true }
                                    );
                                  }}
                                >
                                  Add Agency/Facility
                                </button>
                              </>
                            )}
                          {errors.SubstanceUseTreatmentHistory !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.SubstanceUseTreatmentHistory}
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </>
                      ) : null}
                    </FieldTable>
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="Functioning"
                expandStatus={expandStatus}
                className="mt-2"
                title="MODULE 3: CURRENT FUNCTIONING"
              >
                <Input
                  name="ClientStrengths"
                  label="CLIENT'S CURRENT STRENGTHS AND ACCOMPLISHMENTS:"
                  error={errors.ClientStrengths}
                  value={data.ClientStrengths["@value"]}
                  onChange={handleChange}
                  identical={compare("ClientStrengths")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
                <h5>MENTAL STATUS EXAM</h5>
                <div className="container">
                  <div className="row border-bottom">
                    <div className="col-3">Appearance</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="AppropriateAppearance"
                        label="Appropriate"
                        divClassName="ms-3"
                        checked={
                          data.AppropriateAppearance["@value"] === "true"
                        }
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Neat"
                        label="Neat"
                        divClassName="ms-3"
                        checked={data.Neat["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Disheveled"
                        label="Disheveled"
                        divClassName="ms-3"
                        checked={data.Disheveled["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="BizarreAppearance"
                        label="Bizarre"
                        divClassName="ms-3"
                        checked={data.BizarreAppearance["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherAppearance"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherAppearance["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherAppearance["@value"] === "true" ? (
                        <Input
                          name="OtherAppearanceDetails"
                          label="Comments:"
                          error={errors.OtherAppearanceDetails}
                          value={data.OtherAppearanceDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherAppearanceDetails")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSAppearance !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSAppearance}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Behavior</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="Cooperative"
                        label="Cooperative"
                        divClassName="ms-3"
                        checked={data.Cooperative["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Guarded"
                        label="Guarded"
                        divClassName="ms-3"
                        checked={data.Guarded["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Hyperactive"
                        label="Hyperactive"
                        divClassName="ms-3"
                        checked={data.Hyperactive["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Agitated"
                        label="Agitated"
                        divClassName="ms-3"
                        checked={data.Agitated["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Paranoid"
                        label="Paranoid"
                        divClassName="ms-3"
                        checked={data.Paranoid["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Tics"
                        label="Tics"
                        divClassName="ms-3"
                        checked={data.Tics["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Impulsive"
                        label="Impulsive"
                        divClassName="ms-3"
                        checked={data.Impulsive["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Aggressive"
                        label="Aggressive"
                        divClassName="ms-3"
                        checked={data.Aggressive["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Withdrawn"
                        label="Withdrawn"
                        divClassName="ms-3"
                        checked={data.Withdrawn["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="BizarreBehavior"
                        label="Bizarre"
                        divClassName="ms-3"
                        checked={data.BizarreBehavior["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Rigidity"
                        label="Rigidity"
                        divClassName="ms-3"
                        checked={data.Rigidity["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherBehavior"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherBehavior["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherBehavior["@value"] === "true" ? (
                        <Input
                          name="OtherBehaviorDetails"
                          label="Comments:"
                          error={errors.OtherBehaviorDetails}
                          value={data.OtherBehaviorDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherBehaviorDetails")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSBehavior !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSBehavior}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Speech</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="Normal"
                        label="Normal"
                        divClassName="ms-3"
                        checked={data.Normal["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Slurred"
                        label="Slurred"
                        divClassName="ms-3"
                        checked={data.Slurred["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Pressured"
                        label="Pressured"
                        divClassName="ms-3"
                        checked={data.Pressured["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Tangential"
                        label="Tangential"
                        divClassName="ms-3"
                        checked={data.Tangential["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Incoherent"
                        label="Incoherent"
                        divClassName="ms-3"
                        checked={data.Incoherent["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Stutters"
                        label="Stutters"
                        divClassName="ms-3"
                        checked={data.Stutters["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherSpeech"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherSpeech["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherSpeech["@value"] === "true" ? (
                        <Input
                          name="OtherSpeechDetails"
                          label="Comments:"
                          error={errors.OtherSpeechDetails}
                          value={data.OtherSpeechDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherSpeechDetails")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSSpeech !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSSpeech}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Mood</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="Euthymic"
                        label="Euthymic"
                        divClassName="ms-3"
                        checked={data.Euthymic["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Anxious"
                        label="Anxious"
                        divClassName="ms-3"
                        checked={data.Anxious["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Angry"
                        label="Angry"
                        divClassName="ms-3"
                        checked={data.Angry["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Depressed"
                        label="Depressed"
                        divClassName="ms-3"
                        checked={data.Depressed["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Euphoric"
                        label="Euphoric"
                        divClassName="ms-3"
                        checked={data.Euphoric["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Irritable"
                        label="Irritable"
                        divClassName="ms-3"
                        checked={data.Irritable["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Fearful"
                        label="Fearful"
                        divClassName="ms-3"
                        checked={data.Fearful["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherMood"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherMood["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherMood["@value"] === "true" ? (
                        <Input
                          name="OtherMoodDetails"
                          label="Comments:"
                          error={errors.OtherMoodDetails}
                          value={data.OtherMoodDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherMoodDetails")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSMood !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSMood}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Affect</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="FullRange"
                        label="Full range"
                        divClassName="ms-3"
                        checked={data.FullRange["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Restricted"
                        label="Restricted"
                        divClassName="ms-3"
                        checked={data.Restricted["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Blunted"
                        label="Blunted"
                        divClassName="ms-3"
                        checked={data.Blunted["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Flat"
                        label="Flat"
                        divClassName="ms-3"
                        checked={data.Flat["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Inappropriate"
                        label="Inappropriate"
                        divClassName="ms-3"
                        checked={data.Inappropriate["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherAffect"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherAffect["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherAffect["@value"] === "true" ? (
                        <Input
                          name="OtherAffectDetails"
                          label="Comments:"
                          error={errors.OtherAffectDetails}
                          value={data.OtherAffectDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherAffectDetails")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSAffect !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSAffect}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Thought Process/Perception</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="Logical"
                        label="Logical"
                        divClassName="ms-3"
                        checked={data.Logical["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Preoccupations"
                        label="Preoccupations"
                        divClassName="ms-3"
                        checked={data.Preoccupations["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Delusions"
                        label="Delusions"
                        divClassName="ms-3"
                        checked={data.Delusions["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Hallucinations"
                        label="Hallucinations"
                        divClassName="ms-3"
                        checked={data.Hallucinations["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Racing"
                        label="Racing"
                        divClassName="ms-3"
                        checked={data.Racing["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Disorganized"
                        label="Disorganized"
                        divClassName="ms-3"
                        checked={data.Disorganized["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Concrete"
                        label="Concrete"
                        divClassName="ms-3"
                        checked={data.Concrete["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="SuicidalIdeation"
                        label="Suicidal Ideation"
                        divClassName="ms-3"
                        checked={data.SuicidalIdeation["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="HomicidalIdeation"
                        label="Homicidal Ideation"
                        divClassName="ms-3"
                        checked={data.HomicidalIdeation["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherPerception"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherPerception["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherPerception["@value"] === "true" ? (
                        <Input
                          name="OtherPerceptionDetails"
                          label="Comments:"
                          error={errors.OtherPerceptionDetails}
                          value={data.OtherPerceptionDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherPerceptionDetails")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSPerception !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSPerception}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">
                      Cognition/Intellectual Functioning
                    </div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="NoImpairment"
                        label="No Impairment"
                        divClassName="ms-3"
                        checked={data.NoImpairment["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="ShortAttentionSpan"
                        label="Short Attention Span"
                        divClassName="ms-3"
                        checked={data.ShortAttentionSpan["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="DifficultyConcentrating"
                        label="Difficulty Concentrating"
                        divClassName="ms-3"
                        checked={
                          data.DifficultyConcentrating["@value"] === "true"
                        }
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Distractibility"
                        label="Distractibility"
                        divClassName="ms-3"
                        checked={data.Distractibility["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="MemoryImpairment"
                        label="Memory Impairment"
                        divClassName="ms-3"
                        checked={data.MemoryImpairment["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="LowIntelligence"
                        label="Low Intelligence"
                        divClassName="ms-3"
                        checked={data.LowIntelligence["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherCognition"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherCognition["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherCognition["@value"] === "true" ? (
                        <Input
                          name="OtherCognitionDetails"
                          label="Comments:"
                          error={errors.OtherCognitionDetails}
                          value={data.OtherCognitionDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherCognitionDetails")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSCognition !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSCognition}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Insight/Judgment</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="AppropriateInsight"
                        label="Appropriate"
                        divClassName="ms-3"
                        checked={data.AppropriateInsight["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Fair"
                        label="Fair"
                        divClassName="ms-3"
                        checked={data.Fair["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Impaired"
                        label="Impaired"
                        divClassName="ms-3"
                        checked={data.Impaired["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherInsight"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherInsight["@value"] === "true"}
                        onChange={handleChange}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherInsight["@value"] === "true" ? (
                        <Input
                          name="OtherInsightDetails"
                          label="Comments:"
                          error={errors.OtherInsightDetails}
                          value={data.OtherInsightDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherInsightDetails")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSInsight !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSInsight}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2">
                    <div className="col-3">Comments/Observations</div>
                    <div className="col-9">
                      <TextArea
                        name="MentalStatusComments"
                        error={errors.MentalStatusComments}
                        value={data.MentalStatusComments["@value"]}
                        onChange={handleChange}
                        identical={compare("MentalStatusComments")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
                <br />
                <h5>ASSESSMENT OF RISK OF HARM TO SELF OR OTHERS</h5> (
                <i>
                  If answer to any of the following 5 questions is yes, complete
                  a lifetime C-SSRS Assessment or safety plan.
                </i>
                )
                <ol>
                  <li>
                    Have you ever wished you were dead or wished you could go to
                    sleep and not wake up?
                    <RadioButton
                      name="WishToStopExisting"
                      options={yesNoOptions}
                      error={errors.WishToStopExisting}
                      value={data.WishToStopExisting["@value"]}
                      onChange={handleChange}
                      identical={compare("WishToStopExisting")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                    {data.WishToStopExisting["@value"] === "Yes" ? (
                      <>
                        <Input
                          name="WishToStopExistingComments"
                          label="Comments:"
                          error={errors.WishToStopExistingComments}
                          value={data.WishToStopExistingComments["@value"]}
                          onChange={handleChange}
                          identical={compare("WishToStopExistingComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      </>
                    ) : null}
                  </li>
                  <li>
                    Have you had any thoughts of killing yourself?
                    <RadioButton
                      name="ThoughtsOfSuicide"
                      options={yesNoOptions}
                      error={errors.ThoughtsOfSuicide}
                      value={data.ThoughtsOfSuicide["@value"]}
                      onChange={handleChange}
                      identical={compare("ThoughtsOfSuicide")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                    {data.ThoughtsOfSuicide["@value"] === "Yes" ? (
                      <>
                        <Input
                          name="ThoughtsOfSuicideComments"
                          label="Comments:"
                          error={errors.ThoughtsOfSuicideComments}
                          value={data.ThoughtsOfSuicideComments["@value"]}
                          onChange={handleChange}
                          identical={compare("ThoughtsOfSuicideComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      </>
                    ) : null}
                  </li>
                  <li>
                    Have you ever engaged in self-harm without intent to die?
                    <RadioButton
                      name="HarmedSelf"
                      options={yesNoOptions}
                      error={errors.HarmedSelf}
                      value={data.HarmedSelf["@value"]}
                      onChange={handleChange}
                      identical={compare("HarmedSelf")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                    {data.HarmedSelf["@value"] === "Yes" ? (
                      <>
                        <Input
                          name="HarmedSelfComments"
                          label="Comments:"
                          error={errors.HarmedSelfComments}
                          value={data.HarmedSelfComments["@value"]}
                          onChange={handleChange}
                          identical={compare("HarmedSelfComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      </>
                    ) : null}
                  </li>
                  <li>
                    Have you ever done something reckless/dangerous without
                    concern for your safety?
                    <RadioButton
                      name="RecklessBehavior"
                      options={yesNoOptions}
                      error={errors.RecklessBehavior}
                      value={data.RecklessBehavior["@value"]}
                      onChange={handleChange}
                      identical={compare("RecklessBehavior")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                    {data.RecklessBehavior["@value"] === "Yes" ? (
                      <>
                        <Input
                          name="RecklessBehaviorComments"
                          label="Comments:"
                          error={errors.RecklessBehaviorComments}
                          value={data.RecklessBehaviorComments["@value"]}
                          onChange={handleChange}
                          identical={compare("RecklessBehaviorComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      </>
                    ) : null}
                  </li>
                  <li>
                    Has the youth ever physically harmed, sexually harmed, or
                    forced themself on someone else?
                    <RadioButton
                      name="HarmedOthers"
                      options={yesNoOptions}
                      error={errors.HarmedOthers}
                      value={data.HarmedOthers["@value"]}
                      onChange={handleChange}
                      identical={compare("HarmedOthers")}
                      comments={comments && parsedComments}
                      addComment={() => setShowComment(true)}
                      editComment={toggleCommentModal}
                      deleteComment={handleDeleteComment}
                      acknowledgeComment={handleAcknowledgeComment}
                      disabled={formDisabled}
                    />
                    {data.HarmedOthers["@value"] === "Yes" ? (
                      <>
                        <Input
                          name="HarmedOthersComments"
                          label="Comments:"
                          error={errors.HarmedOthersComments}
                          value={data.HarmedOthersComments["@value"]}
                          onChange={handleChange}
                          identical={compare("HarmedOthersComments")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      </>
                    ) : null}
                  </li>
                </ol>
                {data.WishToStopExisting["@value"] === "Yes" ||
                data.ThoughtsOfSuicide["@value"] === "Yes" ? (
                  <>
                    Based on the response to the above questions, please
                    complete the lifetime{" "}
                    <Link
                      to={`/client/${clientid}?tab=cpass`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      C-SSRS Assessment
                    </Link>
                    .<br />
                  </>
                ) : null}
                {data.HarmedSelf["@value"] === "Yes" ||
                data.RecklessBehavior["@value"] === "Yes" ||
                data.HarmedOthers["@value"] === "Yes" ? (
                  <>
                    Based on the response to the above questions, please
                    complete a{" "}
                    <Link
                      to={`/client/${clientid}?tab=forms`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      safety plan
                    </Link>
                    .<br />
                  </>
                ) : null}
                <br />
                <h5>CENTRAL NERVOUS SYSTEM FUNCTIONAL STATUS</h5>
                <div className="container border">
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Area of Functioning
                    </div>
                    <div className="col p-2">Description</div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">Attention</div>
                    <div className="col p-2">
                      <i>
                        Describe the child's capacity to focus and track
                        contextually-appropriate content. Include information
                        related to both capacity to focus and distractibility.
                      </i>
                      <Input
                        name="Attention"
                        error={errors.Attention}
                        value={data.Attention["@value"]}
                        onChange={handleChange}
                        identical={compare("Attention")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="NoFocus"
                            label="Cannot focus attention for age-appropriate length of time (1 minute per year of age)"
                            checked={data.NoFocus["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="IncompleteTasks"
                            label="Cannot regularly complete tasks"
                            checked={data.IncompleteTasks["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Distracted"
                            label="Easily distracted by extraneous environmental stimuli"
                            checked={data.Distracted["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-8">
                          <Checkbox
                            name="NoShift"
                            label="Cannot shift attention from one task to another (e.g., following directions or ending one activity to begin another)"
                            checked={data.NoShift["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AttentionHypervigilance"
                            label="Demonstrates hypervigilance"
                            checked={
                              data.AttentionHypervigilance["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">Sleep</div>
                    <div className="col p-2">
                      <i>
                        Describe the quality, quantity and pattern of sleep.
                      </i>
                      <Input
                        name="Sleep"
                        error={errors.Sleep}
                        value={data.Sleep["@value"]}
                        onChange={handleChange}
                        identical={compare("Sleep")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="NoSleep"
                            label="Difficulty falling asleep"
                            checked={data.NoSleep["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="NoStaySleep"
                            label="Difficulty staying asleep"
                            checked={data.NoStaySleep["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="NoSleepAlone"
                            label="Difficulty sleeping alone"
                            checked={data.NoSleepAlone["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="SleepHypnogogicPhenomenon"
                            label="Hypnogogic phenomenon"
                            checked={
                              data.SleepHypnogogicPhenomenon["@value"] ===
                              "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Nightmares"
                            label="Nightmares or night terrors"
                            checked={data.Nightmares["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SleepSleepApnea"
                            label="Sleep apnea"
                            checked={data.SleepSleepApnea["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Waking"
                            label="Waking at night"
                            checked={data.Waking["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Wandering"
                            label="Wandering into caregiver bed or around the home"
                            checked={data.Wandering["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Sleepiness"
                            label="Excessive sleepiness during the day"
                            checked={data.Sleepiness["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="SleepCycleReversal"
                            label="Sleep cycle reversal"
                            checked={
                              data.SleepCycleReversal["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">Dissociation</div>
                    <div className="col p-2">
                      <i>
                        Describe the frequency and indications of dissociation
                        demonstrated by the child.
                      </i>
                      <Input
                        name="Dissociation"
                        error={errors.Dissociation}
                        value={data.Dissociation["@value"]}
                        onChange={handleChange}
                        identical={compare("Dissociation")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="SlowBlink"
                            label="Slow eye-blink rate"
                            checked={data.SlowBlink["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="EyesGlazeOver"
                            label="Eyes glaze-over"
                            checked={data.EyesGlazeOver["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="LoseTrackOfTime"
                            label="Child frequently reports losing track of time"
                            checked={data.LoseTrackOfTime["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="NotHeard"
                            label="Child frequently reports or seems to have not heard what others have said"
                            checked={data.NotHeard["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Disconnected"
                            label="Child reports feeling disconnected from body and/or reality"
                            checked={data.Disconnected["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Hallucinate"
                            label="seems to hallucinate"
                            checked={data.Hallucinate["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="PainInsensitivity"
                            label="Has insensitivity to pain"
                            checked={
                              data.PainInsensitivity["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="FrequentProblems"
                            label="Child frequently reports headaches, bladder, or bowel problems"
                            checked={data.FrequentProblems["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Daydreams"
                            label="Frequently daydreams"
                            checked={data.Daydreams["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4">
                          <Checkbox
                            name="DissociationSelfAbuse"
                            label="Demonstrates cutting, picking or other self-abuse"
                            checked={
                              data.DissociationSelfAbuse["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">Arousal</div>
                    <div className="col p-2">
                      <i>
                        Describe how the child demonstrates fight or flight
                        responses.
                      </i>
                      <Input
                        name="Arousal"
                        error={errors.Arousal}
                        value={data.Arousal["@value"]}
                        onChange={handleChange}
                        identical={compare("Arousal")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="IncreasedMuscleTone"
                            label="Increased muscle tone"
                            checked={
                              data.IncreasedMuscleTone["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="IncreasedMotorActivity"
                            label="Increased motor activity"
                            checked={
                              data.IncreasedMotorActivity["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ArousalHypervigilance"
                            label="Demonstrates hypervigilance"
                            checked={
                              data.ArousalHypervigilance["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="SleepProblems"
                            label="Sleep problems"
                            checked={data.SleepProblems["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ArousalImpulsivity"
                            label="Impulsivity"
                            checked={
                              data.ArousalImpulsivity["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="DifficultTransition"
                            label="Demonstrates hypervigilance"
                            checked={
                              data.DifficultTransition["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="MisperceivingCues"
                            label="Misperceiving cues of others"
                            checked={
                              data.MisperceivingCues["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Resistant"
                            label="Resistant/avoidant"
                            checked={data.Resistant["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Defiant"
                            label="Defiant/oppositional"
                            checked={data.Defiant["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4">
                          <Checkbox
                            name="ArousalAggressive"
                            label="Aggressive"
                            checked={
                              data.ArousalAggressive["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">Reward</div>
                    <div className="col p-2">
                      <i>
                        Describe whether the child has the capacity to feel
                        pleasure from healthy relational interactions.
                      </i>
                      <Input
                        name="Reward"
                        error={errors.Reward}
                        value={data.Reward["@value"]}
                        onChange={handleChange}
                        identical={compare("Reward")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="DislikesTouch"
                            label="Doesn't like to be touched"
                            checked={data.DislikesTouch["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="NoValueBehavior"
                            label="Doesn't engage in value-consistent behavior"
                            checked={data.NoValueBehavior["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="DislikesMusic"
                            label="Doesn't like music"
                            checked={data.DislikesMusic["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="UnhealthySex"
                            label="Demonstrates unhealthy sexual behavior"
                            checked={data.UnhealthySex["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Overeating"
                            label="Demonstrates over-eating"
                            checked={data.Overeating["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="RewardSelfAbuse"
                            label="Engages in cutting or other self-harm"
                            checked={data.RewardSelfAbuse["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4">
                          <Checkbox
                            name="RewardSubstanceUse"
                            label="Current alcohol and/or drug abuse"
                            checked={
                              data.RewardSubstanceUse["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Affect Regulation / Mood
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe whether the child has the ability to
                        appropriately express and modulate emotional
                        experiences.
                      </i>
                      <Input
                        name="Mood"
                        error={errors.Mood}
                        value={data.Mood["@value"]}
                        onChange={handleChange}
                        identical={compare("Mood")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="RangeOfEmotions"
                            label="Displays the normal range of emotions (mad, sad, glad, afraid)"
                            checked={data.RangeOfEmotions["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Congruent"
                            label="Emotions are congruent with situations"
                            checked={data.Congruent["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="CorrectResponse"
                            label="Recognizes and correctly responds to own feelings and those of others"
                            checked={data.CorrectResponse["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Nonverbal"
                            label="Understands nonverbal communication"
                            checked={data.Nonverbal["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="MoodAnxious"
                            label="Becomes overly anxious, aggressive or withdrawn when upset"
                            checked={data.MoodAnxious["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="CalmsDown"
                            label="Calms down within 15 minutes when upset &amps; after a period of exciting activity"
                            checked={data.CalmsDown["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Empathy / Attunement
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe whether the child can accurately read and show
                        interest in nonverbal cues of others to discern
                        intentions and feelings before responding appropriately
                        and pro-socially.
                      </i>
                      <Input
                        name="Empathy"
                        error={errors.Empathy}
                        value={data.Empathy["@value"]}
                        onChange={handleChange}
                        identical={compare("Empathy")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Selfish"
                            label="Selfish or inconsiderate behavior, self-serving or oblivious social interactions"
                            checked={data.Selfish["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Acknowledge"
                            label="Acknowledges caregivers presence"
                            checked={data.Acknowledge["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="MakesEyeContact"
                            label="Makes eye contact with spoken to"
                            checked={data.MakesEyeContact["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="SharesFeelings"
                            label="Shares feelings and experiences"
                            checked={data.SharesFeelings["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ShowsAffection"
                            label="Demonstrates affectional feelings toward others (verbally &amp; physically)"
                            checked={data.ShowsAffection["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ComfortsOthers"
                            label="Comforts others when distressed"
                            checked={data.ComfortsOthers["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="CloseRelationships"
                            label="Has close associations with others (a group, family, friends, school and classmates)"
                            checked={
                              data.CloseRelationships["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="RespondsToCaregiver"
                            label="Responds to caregiver's singing, telling stories or playing with them"
                            checked={
                              data.RespondsToCaregiver["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="PlaysWithFriends"
                            label="Plays with or near favored adults"
                            checked={data.PlaysWithFriends["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4">
                          <Checkbox
                            name="ReferencesCaregiver"
                            label="References caregiver while exploring new places or meeting new people"
                            checked={
                              data.ReferencesCaregiver["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Relational / Attachment
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe whether child is able to form and maintain
                        relationships or whether they experience an impaired
                        capacity to share, form and maintain age-appropriate
                        friendships or desire and capacity to engage others.
                      </i>
                      <Input
                        name="RelationalAttachment"
                        error={errors.RelationalAttachment}
                        value={data.RelationalAttachment["@value"]}
                        onChange={handleChange}
                        identical={compare("RelationalAttachment")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="ElicitBehavior"
                            label="Relational cues elicit fear or self-soothing behaviors"
                            checked={data.ElicitBehavior["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="IndiscriminateAffection"
                            label="Indiscriminate affection"
                            checked={
                              data.IndiscriminateAffection["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="AutisticLikeBehaviors"
                            label={`"Autistic" like behaviors`}
                            checked={
                              data.AutisticLikeBehaviors["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Sense of Time / Delay Gratification
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe the child's ability to gauge time and time
                        passing.
                      </i>
                      <Input
                        name="SenseOfTime"
                        error={errors.SenseOfTime}
                        value={data.SenseOfTime["@value"]}
                        onChange={handleChange}
                        identical={compare("SenseOfTime")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col-4">
                          <Checkbox
                            name="EarnReward"
                            label="Engages in one behavior/activity in order to earn reward later"
                            checked={data.EarnReward["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="TrustsReward"
                            label="Understands/trusts that reward will come at a later date/time"
                            checked={data.TrustsReward["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">Communication</div>
                    <div className="col p-2">
                      <i>
                        Describe the child's ability to communicate in both
                        non-verbal and verbal ways. Note both the capacity to
                        convey intent, meaning and purpose as well as accurately
                        perceive and process the intent, meaning and purpose of
                        the communication of others.
                      </i>
                      <Input
                        name="Communication"
                        error={errors.Communication}
                        value={data.Communication["@value"]}
                        onChange={handleChange}
                        identical={compare("Communication")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Self-Awareness / Self-Image
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe how the child functions related to body image,
                        self-esteem, competence relative to others, and how
                        realistic and accurate these self-appraisals are.
                      </i>
                      <Input
                        name="SelfAwareness"
                        error={errors.SelfAwareness}
                        value={data.SelfAwareness["@value"]}
                        onChange={handleChange}
                        identical={compare("SelfAwareness")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="BodyImageDistortions"
                            label="Body image distortions"
                            checked={
                              data.BodyImageDistortions["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Narcissistic"
                            label="Narcissistic behaviors"
                            checked={data.Narcissistic["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SelfDefeating"
                            label="Self-defeating or self-loathing behaviors"
                            checked={data.SelfDefeating["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Non-verbal Cognition
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe the child's functioning related to non-verbal
                        processing, planning and sequencing of information. Note
                        capacity to understand and interpret non-verbal complex
                        relational interactions (e.g., street smarts). Include
                        an IQ score in Performance Domain, if available.
                      </i>
                      <Input
                        name="NonverbalCognition"
                        error={errors.NonverbalCognition}
                        value={data.NonverbalCognition["@value"]}
                        onChange={handleChange}
                        identical={compare("NonverbalCognition")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Modulate Reactivity / Impulsivity
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe the child's ability to regulate behavior or to
                        act on thought rather than impulse.
                      </i>
                      <Input
                        name="ModulateReactivity"
                        error={errors.ModulateReactivity}
                        value={data.ModulateReactivity["@value"]}
                        onChange={handleChange}
                        identical={compare("ModulateReactivity")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Abstract / Reflective Cognition
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe the child's ability to provide insight,
                        self-awareness and to take previously stored information
                        and reconstruct it in creative and abstract ways.
                      </i>
                      <Input
                        name="AbstractCognition"
                        error={errors.AbstractCognition}
                        value={data.AbstractCognition["@value"]}
                        onChange={handleChange}
                        identical={compare("AbstractCognition")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-3 border-end p-2">
                      Values / Beliefs / Morality
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe the development of the child's value system and
                        behavior code. Note the level and complexity of belief
                        system, capacity for moral reasoning, sense of
                        connectedness to others, and the development of complex
                        spiritual and cultural concepts to include
                        internalization as well as understanding their culture's
                        code of ethics and acceptable behavior. <br />
                        <br />
                        **Please note that scoring this lower{" "}
                        <strong>IS NOT SAYING</strong> the child is "immoral" or
                        "bad" - it is more a comment on the maturation of
                        complex forms of abstract cognition.
                      </i>
                      <Input
                        name="Morality"
                        error={errors.Morality}
                        value={data.Morality["@value"]}
                        onChange={handleChange}
                        identical={compare("Morality")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Dishonest"
                            label="Demonstrates dishonesty"
                            checked={data.Dishonest["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Altruistic"
                            label="Demonstrates altruistic behavior"
                            checked={data.Altruistic["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ResistsTemptation"
                            label="Demonstrates resistance to temptation"
                            checked={
                              data.ResistsTemptation["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Delinquent"
                            label="Demonstrates delinquent behavior"
                            checked={data.Delinquent["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="DeviatesCulturalValues"
                            label="Deviates from cultural values"
                            checked={
                              data.DeviatesCulturalValues["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SpiritualPractice"
                            label="Expresses interest in and behaviors relevant to a personal spiritual practice"
                            checked={
                              data.SpiritualPractice["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <h5>EDUCATIONAL INFORMATION</h5>
                <RadioButton
                  name="EducationStatus"
                  error={errors.EducationStatus}
                  value={data.EducationStatus["@value"]}
                  options={educationOptions}
                  onChange={handleChange}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
                {data.EducationStatus["@value"] !==
                "Client has not started school" ? (
                  <div className="container border">
                    <div className="row border-bottom">
                      <div className="col-3 border-end p-2">
                        Area of Functioning
                      </div>
                      <div className="col p-2">Description</div>
                    </div>
                    <div className="row border-bottom">
                      <div className="col-3 border-end p-2">
                        Educational History
                      </div>
                      <div className="col p-2">
                        <i>
                          Indicate schools this youth has attended in the past
                          and include the grade levels attended as well as
                          geographical location, if outside of Las Vegas, for
                          each school (estimates are okay). Please also note if
                          any grades were repeated along with the reason.
                        </i>
                        <Input
                          name="EducationalHistory"
                          error={errors.EducationalHistory}
                          value={data.EducationalHistory["@value"]}
                          onChange={handleChange}
                          identical={compare("EducationalHistory")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row border-bottom">
                      <div className="col-3 border-end p-2">
                        Academic Functioning
                      </div>
                      <div className="col p-2">
                        <i>
                          Include information related to grades, ability to
                          understand and keep up with workload, approximate
                          number of repetitions before information is learned,
                          math and reading/writing skills, and general executive
                          functioning abilities. Include related IQ scores, if
                          available.
                        </i>
                        <Input
                          name="AcademicFunctioning"
                          error={errors.AcademicFunctioning}
                          value={data.AcademicFunctioning["@value"]}
                          onChange={handleChange}
                          identical={compare("AcademicFunctioning")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                        <div className="row">
                          <div className="col">
                            <Checkbox
                              name="RepeatDirections"
                              label="Need to repeat simple directions or provide multiple trials to learn"
                              checked={
                                data.RepeatDirections["@value"] === "true"
                              }
                              onChange={handleChange}
                              comments={comments && parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                              divClassName="ms-3"
                              divStyle={{ width: "200px" }}
                            />
                          </div>
                          <div className="col">
                            <Checkbox
                              name="CauseAndEffect"
                              label="Struggles with simple cause and effect"
                              checked={data.CauseAndEffect["@value"] === "true"}
                              onChange={handleChange}
                              comments={comments && parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                              divClassName="ms-3"
                              divStyle={{ width: "200px" }}
                            />
                          </div>
                          <div className="col">
                            <Checkbox
                              name="Permanence"
                              label="Struggles with permanence"
                              checked={data.Permanence["@value"] === "true"}
                              onChange={handleChange}
                              comments={comments && parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                              divClassName="ms-3"
                              divStyle={{ width: "200px" }}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <Checkbox
                              name="UnderstandMotivations"
                              label="Struggles to understand motivation of others"
                              checked={
                                data.UnderstandMotivations["@value"] === "true"
                              }
                              onChange={handleChange}
                              comments={comments && parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                              divClassName="ms-3"
                              divStyle={{ width: "200px" }}
                            />
                          </div>
                          <div className="col">
                            <Checkbox
                              name="MentalMath"
                              label="Struggles with mental math"
                              checked={data.MentalMath["@value"] === "true"}
                              onChange={handleChange}
                              comments={comments && parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                              divClassName="ms-3"
                              divStyle={{ width: "200px" }}
                            />
                          </div>
                          <div className="col">
                            <Checkbox
                              name="Imagery"
                              label="Struggles to think with imagery"
                              checked={data.Imagery["@value"] === "true"}
                              onChange={handleChange}
                              comments={comments && parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                              divClassName="ms-3"
                              divStyle={{ width: "200px" }}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <Checkbox
                              name="WordProblems"
                              label="Struggles with word problems"
                              checked={data.WordProblems["@value"] === "true"}
                              onChange={handleChange}
                              comments={comments && parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                              divClassName="ms-3"
                              divStyle={{ width: "200px" }}
                            />
                          </div>
                          <div className="col">
                            <Checkbox
                              name="Infographics"
                              label="Struggles with visual infographics"
                              checked={data.Infographics["@value"] === "true"}
                              onChange={handleChange}
                              comments={comments && parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                              divClassName="ms-3"
                              divStyle={{ width: "200px" }}
                            />
                          </div>
                          <div className="col">
                            <Checkbox
                              name="Symbols"
                              label="Struggles with symbol/numeral representations"
                              checked={data.Symbols["@value"] === "true"}
                              onChange={handleChange}
                              comments={comments && parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                              divClassName="ms-3"
                              divStyle={{ width: "200px" }}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <Checkbox
                              name="LargerConcept"
                              label="Struggles to understand how parts/details relate to a larger concept"
                              checked={data.LargerConcept["@value"] === "true"}
                              onChange={handleChange}
                              comments={comments && parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                              divClassName="ms-3"
                              divStyle={{ width: "200px" }}
                            />
                          </div>
                          <div className="col">
                            <Checkbox
                              name="Writing"
                              label="Struggles with writing"
                              checked={data.Writing["@value"] === "true"}
                              onChange={handleChange}
                              comments={comments && parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                              divClassName="ms-3"
                              divStyle={{ width: "200px" }}
                            />
                          </div>
                          <div className="col">
                            <Checkbox
                              name="ConveyMeaning"
                              label="Struggles with verbal communication to convey meaning"
                              checked={data.ConveyMeaning["@value"] === "true"}
                              onChange={handleChange}
                              comments={comments && parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                              divClassName="ms-3"
                              divStyle={{ width: "200px" }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-3 border-end p-2">
                        Behavioral Functioning
                      </div>
                      <div className="col p-2">
                        <i>
                          Include any information related to problems with peers
                          and/or adults as noted by the child and/or teacher and
                          if they've resulted in detention, parent conference,
                          suspension or expulsion.
                        </i>
                        <Input
                          name="BehavioralFunctioning"
                          error={errors.BehavioralFunctioning}
                          value={data.BehavioralFunctioning["@value"]}
                          onChange={handleChange}
                          identical={compare("BehavioralFunctioning")}
                          comments={comments && parsedComments}
                          addComment={() => setShowComment(true)}
                          editComment={toggleCommentModal}
                          deleteComment={handleDeleteComment}
                          acknowledgeComment={handleAcknowledgeComment}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
                <br />
                <h5>MEDICAL INFORMATION</h5>
                <div className="container border">
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Area of Functioning
                    </div>
                    <div className="col p-2">Description</div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">Medical History</div>
                    <div className="col p-2">
                      <i>
                        List any known conditions, dates or age estimates of
                        conditions, treating doctor or facility and location, if
                        outside of Las Vegas (if known).
                      </i>
                      <Input
                        name="MedicalHistory"
                        error={errors.MedicalHistory}
                        value={data.MedicalHistory["@value"]}
                        onChange={handleChange}
                        identical={compare("MedicalHistory")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Current Medical Needs and Services
                    </div>
                    <div className="col p-2">
                      <i>
                        Include any recent referral information requiring
                        follow-up.
                      </i>
                      <Input
                        name="MedicalNeeds"
                        error={errors.MedicalNeeds}
                        value={data.MedicalNeeds["@value"]}
                        onChange={handleChange}
                        identical={compare("MedicalNeeds")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="OccupationalTherapy"
                            label="Occupational Therapy"
                            checked={
                              data.OccupationalTherapy["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="PhysicalTherapy"
                            label="Physical Therapy"
                            checked={data.PhysicalTherapy["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SpeechTherapy"
                            label="Speech Therapy"
                            checked={data.SpeechTherapy["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="FeedingTherapy"
                            label="Feeding Therapy"
                            checked={data.FeedingTherapy["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Medication"
                            label="Daily or as-needed medication"
                            checked={data.Medication["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ImmunizationDeficiency"
                            label="Immunization deficiency"
                            checked={
                              data.ImmunizationDeficiency["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4">
                          <Checkbox
                            name="Precautions"
                            label="Universal precautions"
                            checked={data.Precautions["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="ChronicCondition"
                            label="Chronic medical problems"
                            checked={data.ChronicCondition["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Most Recent Vitals
                    </div>
                    <div className="col p-2">
                      <i>
                        Include heart rate, height, weight and eye-roll score.
                      </i>
                      <Input
                        name="Vitals"
                        error={errors.Vitals}
                        value={data.Vitals["@value"]}
                        onChange={handleChange}
                        identical={compare("Vitals")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Autonomic Regulation
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe lung, skin, gut, pancreas and immune system
                        functioning.
                      </i>
                      <Input
                        name="AutonomicRegulation"
                        error={errors.AutonomicRegulation}
                        value={data.AutonomicRegulation["@value"]}
                        onChange={handleChange}
                        identical={compare("AutonomicRegulation")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="AutonomicRegulationSleepApnea"
                            label="Sleep apnea"
                            checked={
                              data.AutonomicRegulationSleepApnea["@value"] ===
                              "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SIDS"
                            label="History of SIDS-like episodes"
                            checked={data.SIDS["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ExcessiveSighing"
                            label="Excessive sighing"
                            checked={data.ExcessiveSighing["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="ExcessiveYawning"
                            label="Excessive yawning"
                            checked={data.ExcessiveYawning["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Asthma"
                            label="Asthma"
                            checked={data.Asthma["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Diabetes"
                            label="Diabetes"
                            checked={data.Diabetes["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Bloating"
                            label="Bloating"
                            checked={data.Bloating["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Diarrhea"
                            label="Diarrhea"
                            checked={data.Diarrhea["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Constipation"
                            label="Constipation"
                            checked={data.Constipation["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="IBS"
                            label="Irritable bowel"
                            checked={data.IBS["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="UrinaryIncontinence"
                            label="Urinary incontinence"
                            checked={
                              data.UrinaryIncontinence["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="LossOfAppetite"
                            label="Loss of appetite"
                            checked={data.LossOfAppetite["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Dermatitis"
                            label="Dermatitis"
                            checked={data.Dermatitis["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Hives"
                            label="Hives"
                            checked={data.Hives["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SkinSensitivities"
                            label="Skin sensitivities"
                            checked={
                              data.SkinSensitivities["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4">
                          <Checkbox
                            name="DizzinessUponStanding"
                            label="Dizziness upon standing"
                            checked={
                              data.DizzinessUponStanding["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Temperature Regulation and Metabolism
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe the child's metabolic and temperature
                        regulation functions to include any abnormal body
                        temperature (high, low or erratic).
                      </i>
                      <Input
                        name="TempAndMetabolism"
                        error={errors.TempAndMetabolism}
                        value={data.TempAndMetabolism["@value"]}
                        onChange={handleChange}
                        identical={compare("TempAndMetabolism")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="NoWeightGain"
                            label="Eating without gaining weight"
                            checked={data.NoWeightGain["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Obesity"
                            label="Obesity"
                            checked={data.Obesity["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="PickyEating"
                            label="Picky eating"
                            checked={data.PickyEating["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Hoarding"
                            label="Hoarding"
                            checked={data.Hoarding["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Gorging"
                            label="Gorging"
                            checked={data.Gorging["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Purging"
                            label="Purging"
                            checked={data.Purging["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4">
                          <Checkbox
                            name="Vomiting"
                            label="Vomiting"
                            checked={data.Vomiting["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="RefluxRumination"
                            label="Reflux disorder/rumination disorder"
                            checked={data.RefluxRumination["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Extra Occular Eye Movements
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe child's visual functions including eye-blink
                        and eye movement.
                      </i>
                      <Input
                        name="EyeMovement"
                        error={errors.EyeMovement}
                        value={data.EyeMovement["@value"]}
                        onChange={handleChange}
                        identical={compare("EyeMovement")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="EyeMovementTics"
                            label="Tics"
                            checked={data.EyeMovementTics["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="EyeBlinkRate"
                            label="Fast or slow eye-blink rates"
                            checked={data.EyeBlinkRate["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SmoothMovements"
                            label="Difficulty with smooth eye movements"
                            checked={data.SmoothMovements["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Convergence"
                            label="Difficulty with near point convergence of the eyes as an object moves closer to eyes"
                            checked={data.Convergence["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Suck, Swallow, Gag
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe child's functions relating to smooth regulation
                        of mouth, tongue and throat functioning.
                      </i>
                      <Input
                        name="SuckSwallowGag"
                        error={errors.SuckSwallowGag}
                        value={data.SuckSwallowGag["@value"]}
                        onChange={handleChange}
                        identical={compare("SuckSwallowGag")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="ExcessiveSucking"
                            label="Excessive sucking"
                            checked={data.ExcessiveSucking["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ExcessiveSwallowing"
                            label="Excessive swallowing"
                            checked={
                              data.ExcessiveSwallowing["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Gulping"
                            label="Gulping"
                            checked={data.Gulping["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Reflux"
                            label="Reflux"
                            checked={data.Reflux["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Regurgitation"
                            label="Regurgitation"
                            checked={data.Regurgitation["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="RuminationGagging"
                            label="Rumination or gagging when eating or drinking"
                            checked={
                              data.RuminationGagging["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="DifficultyChewing"
                            label="Difficulty chewing food"
                            checked={
                              data.DifficultyChewing["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="DifficultyPills"
                            label="Difficulty taking pills"
                            checked={data.DifficultyPills["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SoftTextures"
                            label="Demands for soft or smooth textures"
                            checked={data.SoftTextures["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="SuckSwallowGagPica"
                            label="Pica"
                            checked={
                              data.SuckSwallowGagPica["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Mouthing"
                            label="Mouthing and tasting objects"
                            checked={data.Mouthing["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="OralFixation"
                            label="Need to chew gum or have something in mouth"
                            checked={data.OralFixation["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Feeding and Appetite
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe the child's appetite and eating related
                        functions.
                      </i>
                      <Input
                        name="Appetite"
                        error={errors.Appetite}
                        value={data.Appetite["@value"]}
                        onChange={handleChange}
                        identical={compare("Appetite")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Rumination"
                            label="Rumination"
                            checked={data.Rumination["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="OddFood"
                            label="Odd food preferences"
                            checked={data.OddFood["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="FoodRefusal"
                            label="Food refusal"
                            checked={data.FoodRefusal["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Anorexia"
                            label="Anorexia and related behaviors"
                            checked={data.Anorexia["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Cravings"
                            label="Insatiable cravings"
                            checked={data.Cravings["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="AppetitePica"
                            label="Pica"
                            checked={data.AppetitePica["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="EmotionalEating"
                            label="Excessive use of food to sooth"
                            checked={data.EmotionalEating["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Fine Motor Skills
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe the child's functioning related to fine motor
                        control, regulation and maturity.
                      </i>
                      <Input
                        name="FineMotorSkills"
                        error={errors.FineMotorSkills}
                        value={data.FineMotorSkills["@value"]}
                        onChange={handleChange}
                        identical={compare("FineMotorSkills")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="HandCoordination"
                            label="Age-inappropriate capacity to grasp, manipulate, reach and handle objects with hands"
                            checked={data.HandCoordination["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Tremors"
                            label="Tremors"
                            checked={data.Tremors["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Drawing"
                            label="Difficulty drawing and handwriting"
                            checked={data.Drawing["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="DrawingShapes"
                            label="Age-appropriate skill in drawing circles, squares, triangles and people"
                            checked={data.DrawingShapes["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="WritesWith"
                            label="Writes with pencil or crayons"
                            checked={data.WritesWith["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ScissorsGlue"
                            label="Cuts with scissors and uses glue normally for age"
                            checked={data.ScissorsGlue["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Utensils"
                            label="Uses utensils correctly for age"
                            checked={data.Utensils["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="FeedsDrinks"
                            label="Feeds and drinks with little trouble"
                            checked={data.FeedsDrinks["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="HandlesManipulatives"
                            label="Handles manipulatives (blocks, stringing beads and construction activities)"
                            checked={
                              data.HandlesManipulatives["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="FoldsPaper"
                            label="Folds paper"
                            checked={data.FoldsPaper["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ButtonsZippers"
                            label="Manages buttons and zippers"
                            checked={data.ButtonsZippers["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Shoes"
                            label="Puts on/ties shoes"
                            checked={data.Shoes["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="PlaysWithClay"
                            label="Creates rolls/shapes with play dough/clay"
                            checked={data.PlaysWithClay["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Large Motor Skills
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe child's ability to crawl, walk, run, skip, hop
                        and stand normally as well as ability to balance self
                        normally for age.
                      </i>
                      <Input
                        name="LargeMotorSkills"
                        error={errors.LargeMotorSkills}
                        value={data.LargeMotorSkills["@value"]}
                        onChange={handleChange}
                        identical={compare("LargeMotorSkills")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="WalksCorrectly"
                            label="Walks heel toe forward/back"
                            checked={data.WalksCorrectly["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="KickThrow"
                            label="Able to age-appropriately kick or throw a ball"
                            checked={data.KickThrow["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Rhythm"
                            label="Demonstrates steady beat consistency or basic rhythm"
                            checked={data.Rhythm["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="DislikesMovements"
                            label="Dislikes or becomes dizzy when spinning, turning, swinging or feet leaving ground"
                            checked={
                              data.DislikesMovements["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="AwkwardMovements"
                            label="Stiff awkward movements"
                            checked={data.AwkwardMovements["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Clumsiness"
                            label="Clumsiness"
                            checked={data.Clumsiness["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Neuroendocrine and Hypothalamic Systems
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe the child's regulation of a wide range of
                        neurohormonal functions including growth hormones, sex
                        steroids, stress-related hormones such as cortisol, and
                        the neuroimmune systems.
                      </i>
                      <Input
                        name="Nueroendocrine"
                        error={errors.Nueroendocrine}
                        value={data.Nueroendocrine["@value"]}
                        onChange={handleChange}
                        identical={compare("Nueroendocrine")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="PrematurePuberty"
                            label="Premature puberty"
                            checked={data.PrematurePuberty["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="LatePuberty"
                            label="Late puberty"
                            checked={data.LatePuberty["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="GrowthAbnormalities"
                            label="Growth abnormalities such as FTT or obesity"
                            checked={
                              data.GrowthAbnormalities["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="LossOfHair"
                            label="Loss of hair"
                            checked={data.LossOfHair["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="TempRegulation"
                            label="Body temperature regulation problems"
                            checked={data.TempRegulation["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="HungerThirst"
                            label="Problems with feeding/hunger and thirst"
                            checked={data.HungerThirst["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="ExcessiveFatigue"
                            label="Excessive fatigue and problems with circadian cycles"
                            checked={data.ExcessiveFatigue["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="HeightProblems"
                            label="Problems with height for age"
                            checked={data.HeightProblems["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ColdIntolerance"
                            label="Cold intolerance"
                            checked={data.ColdIntolerance["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Depression"
                            label="Depression"
                            checked={data.Depression["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ExcessiveWeightLoss"
                            label="Excessive weight loss"
                            checked={
                              data.ExcessiveWeightLoss["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="UncontrolledUrination"
                            label="Uncontrolled urination"
                            checked={
                              data.UncontrolledUrination["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="InabilityToSmell"
                            label="Inability to smell"
                            checked={data.InabilityToSmell["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Primary Sensory Integration
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe the child's functioning involving the
                        processing and integration of primary sensory input such
                        as touch, sound, vision, smell and taste.
                      </i>
                      <Input
                        name="PrimarySensoryIntegration"
                        error={errors.PrimarySensoryIntegration}
                        value={data.PrimarySensoryIntegration["@value"]}
                        onChange={handleChange}
                        identical={compare("PrimarySensoryIntegration")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="TouchHypersensitivity"
                            label="Touch hypersensitivity"
                            checked={
                              data.TouchHypersensitivity["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="TouchDefensiveness"
                            label="Touch defensiveness"
                            checked={
                              data.TouchDefensiveness["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Overwhelmed"
                            label="Easily overwhelmed by loud or chaotic experiences (e.g., birthday parties, malls)"
                            checked={data.Overwhelmed["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="SensoryIssues"
                            label="Difficulty gaining information from sensory experiences or using this information (touch, smell, sound, sight)"
                            checked={data.SensoryIssues["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="MouthsObjects"
                            label="Mouths objects (pencils, shirt, hands)"
                            checked={data.MouthsObjects["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ChewsNonFood"
                            label="Chews, licks non-food items"
                            checked={data.ChewsNonFood["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="IncessantTouching"
                            label="Touches, pokes, pushes objects or others incessantly"
                            checked={
                              data.IncessantTouching["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="PrimarySensoryIntegrationDislikesTouch"
                            label="Dislikes being touched"
                            checked={
                              data.PrimarySensoryIntegrationDislikesTouch[
                                "@value"
                              ] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="DislikesMisc"
                            label="Dislikes shoes, clothing tags, textures or messy things"
                            checked={data.DislikesMisc["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">Psychosexual</div>
                    <div className="col p-2">
                      <i>
                        Describe the child's functioning related to maturation
                        and manifestation of sexual identity and behaviors.
                        Central to these functions is the development of
                        associations between intimacy and sexuality.
                      </i>
                      <Input
                        name="Psychosexual"
                        error={errors.Psychosexual}
                        value={data.Psychosexual["@value"]}
                        onChange={handleChange}
                        identical={compare("Psychosexual")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="GenderExpression"
                            label="Struggles with gender expression and identity acceptance"
                            checked={data.GenderExpression["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="AbnormalSexualPlay"
                            label="Abnormal preoccupation with sexual play"
                            checked={
                              data.AbnormalSexualPlay["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Predatory"
                            label="Sexual predatory behaviors"
                            checked={data.Predatory["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Masturbation"
                            label="Compulsive or indiscrete masturbation"
                            checked={data.Masturbation["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Pornography"
                            label="Preoccupation with pornography"
                            checked={data.Pornography["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SexualActingOut"
                            label="Sexualized acting out"
                            checked={data.SexualActingOut["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-3 border-end p-2">
                      Somatosensory and Motorsensory
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe the child's multisensory capacities which allow
                        the creation of a complex multidimensional
                        representation of experience; these derive from the
                        capacity to organize various physical sensations from
                        the body and the environment mediated by systems in
                        lower parts of the brain.
                      </i>
                      <Input
                        name="Somatosensory"
                        error={errors.Somatosensory}
                        value={data.Somatosensory["@value"]}
                        onChange={handleChange}
                        identical={compare("Somatosensory")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="MotorDyscoordination"
                            label="Fine or large motor dyscoordination"
                            checked={
                              data.MotorDyscoordination["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Dyspraxia"
                            label="Dyspraxia"
                            checked={data.Dyspraxia["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Miswiring"
                            label="Unusual mis-wiring of various sensory inputs"
                            checked={data.Miswiring["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="SensoryMisperceptions"
                            label="Sensory misperceptions"
                            checked={
                              data.SensoryMisperceptions["@value"] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SomatosensoryHypnogogicPhenomenon"
                            label="Hypnogogic phenomenon"
                            checked={
                              data.SomatosensoryHypnogogicPhenomenon[
                                "@value"
                              ] === "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SomatosensoryHallucinations"
                            label="Hallucinations"
                            checked={
                              data.SomatosensoryHallucinations["@value"] ===
                              "true"
                            }
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-3 border-end p-2">
                      Speech and Articulation
                    </div>
                    <div className="col p-2">
                      <i>
                        Describe the child's functions involved in the oral
                        communication.
                      </i>
                      <Input
                        name="Speech"
                        error={errors.Speech}
                        value={data.Speech["@value"]}
                        onChange={handleChange}
                        identical={compare("Speech")}
                        comments={comments && parsedComments}
                        addComment={() => setShowComment(true)}
                        editComment={toggleCommentModal}
                        deleteComment={handleDeleteComment}
                        acknowledgeComment={handleAcknowledgeComment}
                        disabled={formDisabled}
                      />
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Volume"
                            label="Problems with the volume, clarity and accuracy of speech"
                            checked={data.Volume["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="CertainSounds"
                            label="Difficulties making certain sounds"
                            checked={data.CertainSounds["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Enunciation"
                            label="Unclear enunciation"
                            checked={data.Enunciation["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Mumbling"
                            label="Mumbling"
                            checked={data.Mumbling["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Muttering"
                            label="Muttering"
                            checked={data.Muttering["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Stuttering"
                            label="Stuttering"
                            checked={data.Stuttering["@value"] === "true"}
                            onChange={handleChange}
                            comments={comments && parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                            divClassName="ms-3"
                            divStyle={{ width: "200px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="ClinicalSummary"
                expandStatus={expandStatus}
                className="mt-2"
                title="MODULE 4: DIAGNOSIS AND CLINICAL SUMMARY"
              >
                <h5>Assessment Results</h5>
                <RadioButton
                  name="AssessmentType"
                  error={errors.AssessmentType}
                  value={data.AssessmentType["@value"]}
                  options={assessmentTypeOptions}
                  onChange={handleChange}
                  identical={compare("AssessmentType")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
                {data.AssessmentType["@value"] !== undefined && (
                  <Input
                    name="AssessmentScore"
                    label="Score:"
                    error={errors.AssessmentScore}
                    value={data.AssessmentScore["@value"]}
                    onChange={handleChange}
                    identical={compare("AssessmentScore")}
                    comments={comments && parsedComments}
                    addComment={() => setShowComment(true)}
                    editComment={toggleCommentModal}
                    deleteComment={handleDeleteComment}
                    acknowledgeComment={handleAcknowledgeComment}
                    disabled={formDisabled}
                  />
                )}
                <RadioButton
                  name="SED"
                  label="SED:"
                  error={errors.SED}
                  value={data.SED["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  identical={compare("SED")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
                <TextArea
                  name="ClinicalNarrativeSummary"
                  label="Clinical Narrative Summary"
                  error={errors.ClinicalNarrativeSummary}
                  value={data.ClinicalNarrativeSummary["@value"]}
                  onChange={handleChange}
                  identical={compare("ClinicalNarrativeSummary")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
                <FieldTable
                  name="DiagnosisDetails"
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                >
                  Diagnosis
                  <br />
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col-3 border-end py-2">ICD-10 Code</div>
                      <div className="col py-2">Diagnosis</div>
                    </div>
                    {data.DiagnosisDetails["@value"] &&
                      JSON.parse(data.DiagnosisDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col-3 border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(index, "DiagnosisDetails");
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "DiagnosisDetails"
                                      );
                                    }}
                                  ></i>
                                  <TextArea
                                    name="Code"
                                    value={item.Code}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "DiagnosisDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(index, "DiagnosisDetails");
                                    }}
                                  ></i>
                                  {item.Code}
                                </>
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <TextArea
                                  name="Diagnosis"
                                  value={item.Diagnosis}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "DiagnosisDetails"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Diagnosis
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {formsyDetails.Active === "1" &&
                    formsyDetails.FinalSigned !== "1" && (
                      <>
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("DiagnosisDetails", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Diagnosis
                        </button>
                      </>
                    )}
                  {errors.DiagnosisDetails !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.DiagnosisDetails}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
                <Input
                  name="TreatmentExpectations"
                  label="Family and client expectations for treatment:"
                  error={errors.TreatmentExpectations}
                  value={data.TreatmentExpectations["@value"]}
                  onChange={handleChange}
                  identical={compare("TreatmentExpectations")}
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                />
                <FieldTable
                  name="TreatmentRecommendations"
                  comments={comments && parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                >
                  Treatment Recommendations
                  <br />
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col py-2 border-end">Recommendation</div>
                      <div className="col py-2">Comments</div>
                    </div>
                    {data.TreatmentRecommendations["@value"] &&
                      JSON.parse(data.TreatmentRecommendations["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col py-2 border-end d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                  ></i>
                                  <Select
                                    name="Recommendation"
                                    value={item.Recommendation}
                                    options={treatmentRecommendationOptions}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                  {item.Recommendation === "Other" ? (
                                    <Input
                                      name="RecommendationOther"
                                      value={item.RecommendationOther}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "TreatmentRecommendations"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : null}
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                  ></i>
                                  {item.Recommendation !== "Other"
                                    ? item.Recommendation
                                    : item.RecommendationOther !== undefined &&
                                      item.RecommendationOther !== ""
                                    ? item.RecommendationOther
                                    : "Other"}
                                </>
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Comments"
                                  value={item.Comments}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "TreatmentRecommendations"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Comments
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {formsyDetails.Active === "1" &&
                    formsyDetails.FinalSigned !== "1" && (
                      <>
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("TreatmentRecommendations", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Treatment Recommendation
                        </button>
                      </>
                    )}
                  {errors.TreatmentRecommendations !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.TreatmentRecommendations}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
              </ExpandDiv>
              {showNarrative === "Show" ? (
                <Form.Group className="clinicalNarrative bg-white border p-2">
                  <Form.Label>
                    <b>Clinical Narrative Summary:</b>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="ClinicalNarrativeSummary"
                    value={data.ClinicalNarrativeSummary["@value"]}
                    onChange={handleChange}
                    disabled={formDisabled}
                    rows="5"
                    style={{ maxHeight: "120px" }}
                    type="textarea"
                  />
                </Form.Group>
              ) : null}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default NVCMHA;
